// src/pages/CustomersPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import './css/InventoryPage.css';
import './css/InvoicesPage.css';
import './css/TransfersPage.css';
import './css/RepairOrdersPage.css';
import TopNav from '../components/TopNav';
import CustomerCrm from '../components/CustomerCrm';
import AlertModal from '../components/AlertModal';
import CollectionReportEmailActions from '../components/CollectionReportEmailActions';
import CollectionReportDownload from '../components/CollectionReportDownload';
import InvoicePrintEmailActions from '../components/InvoicePrintEmailActions';
import useBusinessAddress from '../hooks/useBusinessAddress';
import { useAuth } from '../context/AuthContext';
import {
  dateSortValue,
  formatCalendarDate,
  formatDateInputValue,
  formatDateTimeConsistent,
} from '../utils/dateDisplay';
import {
  ShipDocInvoiceDocument,
  TransferPrintLabel,
  getTransferInvoiceTitle,
  printTransferLabel,
  transferToEmailInvoice,
} from './TransfersPage';
import { can, hasPermission } from '../accessControl';

const formatDate = (value) => {
  return formatCalendarDate(value);
};

const formatDateTime = (value) => {
  return formatDateTimeConsistent(value);
};

const LOCAL_TIMESTAMP_PATTERN = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/;

const formatLocalTimestampText = (value) => {
  if (!value) return '';
  const match = String(value).trim().match(LOCAL_TIMESTAMP_PATTERN);
  if (!match) return '';
  const [, year, month, day, hourText, minute, secondText = '00'] = match;
  const hour = Number(hourText);
  const second = Number(secondText);
  if (!Number.isFinite(hour) || !Number.isFinite(second)) return '';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  const secondDisplay = second ? `:${String(second).padStart(2, '0')}` : '';
  return `${Number(month)}/${Number(day)}/${year}, ${hour12}:${minute}${secondDisplay} ${suffix}`;
};

const transactionTimestampDisplay = (value) => {
  const formatted = formatLocalTimestampText(value) || formatDateTime(value);
  if (!formatted) return { primary: '—', secondary: '' };
  const [primary, ...secondaryParts] = formatted.split(', ');
  return {
    primary,
    secondary: secondaryParts.join(', '),
  };
};

const renderTransactionTimestamp = (value) => {
  const display = transactionTimestampDisplay(value);
  return (
    <>
      {display.primary}
      {display.secondary && <small className="transfer-date-note">{display.secondary}</small>}
    </>
  );
};

const formatTransactionTimestampText = (value) => (
  formatLocalTimestampText(value) || formatDateTime(value)
);

const isRepairInvoiceDocument = (invoice) => (
  Number(invoice?.transactionTypeId) === 640
  || String(invoice?.type || '').toLowerCase().includes('repair')
  || /^RO-/i.test(String(invoice?.invoiceNo || ''))
);

const customerDisplayName = (customer) => (
  customer?.name
  || [customer?.firstname, customer?.lastname].filter(Boolean).join(' ')
  || customer?.companyname
  || customer?.fullName
  || ''
);

const customerBillTo = (customer, fallbackEmail = '') => ({
  name: customerDisplayName(customer),
  addr1: customer?.bill_addr_1 || customer?.billaddr1 || customer?.address || '',
  addr2: customer?.bill_addr_2 || customer?.billaddr2 || '',
  addr3: customer?.bill_addr_3 || customer?.billaddr3 || '',
  addr4: customer?.bill_addr_4 || customer?.billaddr4 || '',
  city: customer?.bill_city || customer?.billcity || '',
  state: customer?.bill_state || customer?.billstate || '',
  zip: customer?.bill_zip || customer?.billzip || '',
  country: customer?.bill_country || customer?.billcountry || '',
  phone: customer?.phone || customer?.altphone || '',
  email: customer?.email || fallbackEmail || '',
});

const paymentDetailToInvoiceDocument = ({ documentId, detail, customer, businessAddress, fallbackEmail = '' }) => {
  const invoiceNo = String(documentId || detail?.invoiceNo || detail?.documentId || '').replace(/^RO-/i, '');
  const items = (detail?.items || [])
    .filter((item) => !['shipping-invoice-tax', 'custom-invoice-tax'].includes(String(item.lineKey || item.id || '')))
    .map((item, index) => {
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const price = Number(item.price ?? item.lineTotal ?? 0) || 0;
    return {
      id: item.id || item.allocationKey || `payment-detail-item-${index}`,
      description: item.description || 'Imported invoice balance',
      serial: item.serialNo || item.serial || item.referenceNo || '',
      quantity,
      price,
    };
  });
  const totalAmount = Number(detail?.originalAmount ?? items.reduce((sum, item) => sum + (Number(item.quantity || 1) * Number(item.price || 0)), 0));
  const to = customerBillTo(customer, fallbackEmail);
  return {
    id: invoiceNo,
    invoiceNo,
    docNo: invoiceNo,
    date: detail?.date,
    type: 'Invoice',
    documentTitle: 'Invoice',
    transactionTypeId: 4,
    from: businessAddress,
    businessAddress,
    to,
    billTo: to,
    clientName: to.name,
    customerName: to.name,
    items,
    totalAmount,
    taxAmount: Number(detail?.taxAmount || 0),
    payments: Number(detail?.paidAmount || 0),
    credits: 0,
    balanceDue: Number(detail?.amountDue ?? Math.max(0, totalAmount - Number(detail?.paidAmount || 0))),
    paidDate: detail?.paidDate || null,
  };
};

const invoiceTaxSubtotal = (invoice) => {
  const explicitSubtotal = Number(invoice?.subtotalAmount ?? invoice?.subtotal);
  if (Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0) return explicitSubtotal;
  const items = (invoice?.items || []).filter((item) => !['shipping-invoice-tax', 'custom-invoice-tax'].includes(String(item.lineKey || item.id || '')));
  const lineSubtotal = items.reduce((sum, item) => {
    if (Number.isFinite(Number(item.actualTotal))) return sum + Math.abs(Number(item.actualTotal));
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const price = Number(item.price ?? item.costPer ?? item.actualPrice ?? item.lineTotal ?? 0) || 0;
    return sum + (quantity * price);
  }, 0);
  if (lineSubtotal > 0) return lineSubtotal;
  return Math.max(0, Number(invoice?.totalAmount ?? invoice?.total ?? 0) - Number(invoice?.taxAmount || 0));
};

const taxAmountForRate = (invoice, rate) => Math.round(invoiceTaxSubtotal(invoice) * Number(rate || 0)) / 100;

const mergePaymentDetailIntoInvoiceDocument = (document, detail) => {
  if (!document || !detail) return document;
  const detailItems = detail.items || [];
  const hasImportedAmounts = detailItems.some((item) => Number(item.lineTotal ?? item.price ?? 0) > 0)
    || Number(detail.taxAmount || 0) > 0;
  if (!hasImportedAmounts) return document;
  const mergedItems = (document.items || []).map((item, index) => {
    const detailItem = detailItems[index];
    const detailAmount = Number(detailItem?.lineTotal ?? detailItem?.price ?? 0);
    if (!detailItem || detailAmount <= 0) return item;
    const quantity = Math.abs(Number(item.quantity) || 0) || 1;
    const unitAmount = detailAmount / quantity;
    return {
      ...item,
      price: unitAmount,
      costPer: unitAmount,
      actualPrice: unitAmount,
      actualTotal: detailAmount,
      lineTotal: detailAmount,
      totalCost: detailAmount,
    };
  });
  const totalAmount = Number(detail.originalAmount ?? document.totalAmount ?? document.total ?? 0);
  return {
    ...document,
    items: mergedItems,
    total: totalAmount,
    totalAmount,
    taxAmount: Number(detail.taxAmount || document.taxAmount || 0),
    payments: Number(detail.paidAmount ?? document.payments ?? 0),
    credits: 0,
    balanceDue: Number(detail.amountDue ?? document.balanceDue ?? 0),
    paidDate: detail.paidDate || document.paidDate || null,
  };
};

const formatDateInput = (value) => {
  return formatDateInputValue(value);
};

const getTodayDateInputValue = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const getDefaultInvoiceDateRange = () => ({
  start: '',
  end: getTodayDateInputValue(),
});

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const serialListText = (item) => {
  const serials = item?.serials?.length ? item.serials : [item?.serial].filter(Boolean);
  return serials.join(', ');
};

const groupReceiveItems = (items = []) => {
  const groups = new Map();

  items.forEach((item, index) => {
    const key = [
      item.lineNo,
      item.itemId,
      item.item,
      item.description,
      item.vendorPart,
      item.uom,
      item.poNo,
    ].join('|');
    const current = groups.get(key) || {
      ...item,
      id: `group-${item.lineNo || index}-${item.itemId || item.item || index}`,
      quantity: 0,
      totalCost: 0,
      serials: [],
    };
    const serial = String(item.serial || '').trim();

    current.quantity += Number(item.quantity) || 0;
    current.totalCost += Number(item.totalCost) || 0;
    current.ordered = item.ordered;
    current.backOrder = item.backOrder;
    current.costPer = Number(item.costPer) || current.costPer || 0;
    if (serial && !current.serials.includes(serial)) {
      current.serials.push(serial);
    }
    groups.set(key, current);
  });

  return Array.from(groups.values());
};

const normalizeCustomerProfile = (customer) => {
  if (!customer) return customer;

  const rawType = customer.customertypeid ?? customer.customerTypeId ?? customer.type ?? '';
  const customerTypeId = rawType === null || rawType === undefined || rawType === ''
    ? ''
    : String(rawType);

  return {
    ...customer,
    type: customerTypeId,
    customertypeid: customerTypeId,
    customerTypeId,
    sales_tax_certificate: customer.sales_tax_certificate ?? customer.resale ?? '',
    resale: customer.resale ?? customer.sales_tax_certificate ?? '',
    notes: customer.notes ?? '',
    sendTransferSaleEmail: customer.sendTransferSaleEmail === true,
    contact: customer.contact ?? '',
    altcontact: customer.altcontact ?? '',
    phone: customer.phone ?? '',
    altphone: customer.altphone ?? '',
    fax: customer.fax ?? '',
    website: customer.website ?? '',
    email: customer.email ?? '',
    consignmentBalanceLimit: customer.consignmentBalanceLimit ?? '',
    agedInventoryDaysLimit: customer.agedInventoryDaysLimit ?? '',
    inventoryPiecesLimit: customer.inventoryPiecesLimit ?? '',
    sameItemsLimit: customer.sameItemsLimit ?? '',
  };
};

const customerProfileSaveFields = [
  ['name', 'name'],
  ['fullname', 'fullname'],
  ['firstname', 'firstname'],
  ['lastname', 'lastname'],
  ['companyname', 'companyname'],
  ['billaddr1', 'billaddr1'],
  ['billaddr2', 'billaddr2'],
  ['billaddr3', 'billaddr3'],
  ['billaddr4', 'billaddr4'],
  ['billcity', 'billcity'],
  ['billstate', 'billstate'],
  ['billzip', 'billzip'],
  ['billcountry', 'billcountry'],
  ['shipaddr1', 'shipaddr1'],
  ['shipaddr2', 'shipaddr2'],
  ['shipaddr3', 'shipaddr3'],
  ['shipaddr4', 'shipaddr4'],
  ['shipcity', 'shipcity'],
  ['shipstate', 'shipstate'],
  ['shipzip', 'shipzip'],
  ['shipcountry', 'shipcountry'],
  ['customertypeid', 'customertypeid'],
  ['resale', 'resale'],
  ['notes', 'notes'],
  ['sendTransferSaleEmail', 'sendTransferSaleEmail'],
  ['contact', 'contact'],
  ['altcontact', 'altcontact'],
  ['phone', 'phone'],
  ['altphone', 'altphone'],
  ['fax', 'fax'],
  ['website', 'website'],
  ['email', 'email'],
  ['consignmentBalanceLimit', 'consignmentBalanceLimit'],
  ['agedInventoryDaysLimit', 'agedInventoryDaysLimit'],
  ['inventoryPiecesLimit', 'inventoryPiecesLimit'],
  ['sameItemsLimit', 'sameItemsLimit'],
];

const profileValue = (value) => (value === null || value === undefined ? '' : String(value));

const buildCustomerProfileUpdatePayload = (currentCustomer = {}, editedCustomer = {}) => (
  customerProfileSaveFields.reduce((payload, [payloadKey, customerKey]) => {
    const currentValue = profileValue(currentCustomer[customerKey]);
    const editedValue = profileValue(editedCustomer[customerKey]);
    if (currentValue !== editedValue) {
      payload[payloadKey] = editedCustomer[customerKey] ?? '';
    }
    return payload;
  }, {})
);

const hasCustomerShippingAddress = (customer = {}) => [
  customer.shipaddr1,
  customer.shipaddr2,
  customer.shipaddr3,
  customer.shipaddr4,
  customer.shipcity,
  customer.shipstate,
  customer.shipzip,
].some((value) => String(value || '').trim());

const getCustomerLabelLocation = (customer, addressType = 'billing') => {
  if (!customer) return null;
  const prefix = addressType === 'shipping' && hasCustomerShippingAddress(customer) ? 'ship' : 'bill';
  const name = customer.name
    || [customer.firstname, customer.lastname].filter(Boolean).join(' ')
    || customer.companyname
    || '';
  return {
    name,
    address1: customer[`${prefix}addr1`] || '',
    address2: customer[`${prefix}addr2`] || '',
    address3: customer[`${prefix}addr3`] || '',
    address4: customer[`${prefix}addr4`] || '',
    city: customer[`${prefix}city`] || '',
    state: customer[`${prefix}state`] || '',
    zip: customer[`${prefix}zip`] || '',
    phone: customer.phone || customer.altphone || '',
  };
};

const printCustomerLabel = (customer, pickup = false, addressType = 'billing') => {
  const location = getCustomerLabelLocation(customer, addressType);
  if (!location) return;
  const name = location.name;
  const addressLines = [location.address1, location.address2, location.address3, location.address4]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const cityLine = [location.city, location.state].filter(Boolean).join(' ');
  const phone = String(location.phone || '').trim();
  const normalizedPhone = phone.replace(/\D/g, '');
  const isPhoneLine = (value) => normalizedPhone && String(value || '').replace(/\D/g, '') === normalizedPhone;
  const normalizeLabelLine = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const normalizedName = normalizeLabelLine(name);
  const isNameLine = (value) => normalizedName && normalizeLabelLine(value) === normalizedName;
  const shippingAddressLines = addressLines.filter((line) => !isPhoneLine(line) && !isNameLine(line));
  const labelClass = pickup ? 'customer-print-label customer-print-label--pickup' : 'customer-print-label';

  const iframe = document.createElement('iframe');
  iframe.title = pickup ? 'Pick Up label print output' : 'Delivery label print output';
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '384px';
  iframe.style.height = '576px';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => setTimeout(() => iframe.remove(), 250);
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <!doctype html>
    <html>
      <head>
        <title>${pickup ? 'Pick Up' : 'Shipping'} Label - ${escapeHtml(name)}</title>
        <style>
          @page { size: 4in 6in; margin: 0; }
          * { box-sizing: border-box; }
          html, body { background: #fff; margin: 0; padding: 0; }
          .customer-print-label {
            align-items: center;
            background: #fff;
            color: #111;
            display: flex;
            flex-direction: column;
            font-family: Arial, Helvetica, sans-serif;
            height: 6in;
            justify-content: space-between;
            overflow: hidden;
            padding: 0.48in 0.2in 0.38in;
            text-align: center;
            text-transform: uppercase;
            width: 4in;
          }
          .customer-print-label--pickup {
            align-items: flex-start;
            border: 1px solid #111;
            justify-content: flex-start;
            padding: 0.26in 0.22in;
            text-align: left;
          }
          .customer-print-address { display: flex; flex-direction: column; font-size: 0.38in; font-weight: 700; line-height: 1.2; width: 100%; }
          .customer-print-name { font-size: 0.38in; line-height: 1.1; margin-bottom: 0.18in; }
          .customer-print-break-before { margin-top: 0.32in; }
          .customer-print-label--pickup .customer-print-address {
            align-items: flex-start;
            font-size: 0.38in;
            line-height: 1.15;
            width: auto;
          }
          .customer-print-label--pickup .customer-print-name {
            font-size: 0.38in;
            line-height: 1.05;
            margin: 0 0 0.38in;
          }
          .customer-print-pickup {
            -webkit-print-color-adjust: exact;
            align-self: center;
            background: #000;
            color: #fff;
            display: inline-block;
            font-size: 0.3in;
            font-weight: 900;
            line-height: 1.05;
            margin-left: 0;
            padding: 0.06in 0.24in;
            print-color-adjust: exact;
            text-align: center;
          }
          .customer-print-pickup-phone {
            align-self: center;
            font-size: 0.38in;
            font-weight: 800;
            letter-spacing: 0.01in;
            line-height: 1.1;
            margin-top: 0.35in;
          }
        </style>
      </head>
      <body>
        <article class="${labelClass}">
          ${pickup
            ? `<div class="customer-print-address">
                <strong class="customer-print-name">${escapeHtml(name)}</strong>
                <div class="customer-print-pickup">*PICKUP<br>ONLY*</div>
                ${phone ? `<div class="customer-print-pickup-phone">${escapeHtml(phone)}</div>` : ''}
              </div>`
            : `<div class="customer-print-address">
                <strong class="customer-print-name">${escapeHtml(name)}</strong>
                ${shippingAddressLines.map((line) => `<span>${escapeHtml(line)}</span>`).join('')}
                ${cityLine ? `<span class="customer-print-break-before">${escapeHtml(cityLine)}</span>` : ''}
                ${phone ? `<span class="customer-print-break-before">${escapeHtml(phone)}</span>` : ''}
              </div>
              <div></div>`}
        </article>
      </body>
    </html>
  `);
  doc.close();
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
    iframe.contentWindow.print();
    setTimeout(cleanup, 1000);
  }, 150);
};

const phoneDigits = (value) => String(value || '').replace(/\D/g, '');

const getCustomerPhoneFields = (customer = {}) => [
  customer.phone,
  customer.altphone,
  customer.billaddr2,
  customer.billaddr3,
  customer.shipaddr2,
  customer.shipaddr3,
  ...(Array.isArray(customer.addresses) ? customer.addresses.map((address) => address.phone) : []),
];

const customerMatchesSearch = (customer, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;

  const queryDigits = phoneDigits(q);
  const name = String(customer.name || '').toLowerCase();
  const fullName = String(customer.fullname || customer.full_name || '').toLowerCase();
  const phoneMatch = queryDigits.length > 0 && getCustomerPhoneFields(customer)
    .some((value) => phoneDigits(value).includes(queryDigits));

  return (
    name.includes(q) ||
    fullName.includes(q) ||
    phoneMatch
  );
};

const getDefaultCustomerTypeId = (types = []) => {
  const retail = types.find((type) => String(type.name || '').trim().toLowerCase() === 'retail');
  return retail ? String(retail.id) : (types[0]?.id ? String(types[0].id) : '');
};

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5h16l-6.5 7.5V18l-3 1v-6.5L4 5z" />
  </svg>
);

const CustomersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedCustomerId = searchParams.get('customerId');
  const crmNavigationKey = useLocation().key;
  const linkedTab = searchParams.get('tab');
  const businessAddress = useBusinessAddress();
  const { user } = useAuth();
  const [newEmployee, setNewEmployee] = useState({
    companyname: '',
    customertypeid: '',
    phone: '',
    email: '',
  });

  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [customerAddStatus, setCustomerAddStatus] = useState('');
  const [duplicatePhoneCustomers, setDuplicatePhoneCustomers] = useState([]);
  const [employees, setCustomers] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [updateCustomer, setUpdateCustomer] = useState(null);
  const canOpenCreateCustomer = hasPermission(user, 'customers.create');
  const creatableCustomerTypes = useMemo(
    () => customerTypes.filter((type) => can(user, 'customers.create', {
      customerTypeName: type.name,
      typeName: type.name,
    })),
    [customerTypes, user]
  );
  const canCreateCustomer = canOpenCreateCustomer && creatableCustomerTypes.length > 0;
  const canEditSelectedCustomer = can(user, 'customers.edit', selectedCustomer);
  const canDeleteCustomer = can(user, 'customers.delete', selectedCustomer);
  const canViewCustomerProfile = can(user, 'customers.read');
  const canViewCustomerCrm = canViewCustomerProfile && can(user, 'customers.crm', selectedCustomer);
  const canViewPayments = can(user, 'payments.read');
  const canViewShipDocs = can(user, 'locations.read');
  const canViewTransfers = can(user, 'invoices.read');
  const canViewRepairOrders = can(user, 'repairs.read');
  const [filteredCustomers, setFilteredCustomers] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [orderDateRange, setOrderDateRange] = useState(getDefaultInvoiceDateRange);
  const [shipDocInvoiceFilter, setShipDocInvoiceFilter] = useState('');
  const [shipDocInvoiceSortDirection, setShipDocInvoiceSortDirection] = useState('desc');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState('');
  const [invoicesSort, setInvoicesSort] = useState({ key: '', direction: 'asc' });
  const [invoiceDateRange, setInvoiceDateRange] = useState(getDefaultInvoiceDateRange);
  const [repairOrders, setRepairOrders] = useState([]);
  const [repairOrdersLoading, setRepairOrdersLoading] = useState(false);
  const [repairOrdersError, setRepairOrdersError] = useState('');
  const [collectionReport, setCollectionReport] = useState(null);
  const [collectionReportLoading, setCollectionReportLoading] = useState(false);
  const [collectionReportError, setCollectionReportError] = useState('');
  const [collectionReportSort, setCollectionReportSort] = useState({ key: 'date', direction: 'asc' });
  const [collectionItemFilter, setCollectionItemFilter] = useState('');
  const [collectionSerialFilter, setCollectionSerialFilter] = useState('');
  const [collectionItemFilterOpen, setCollectionItemFilterOpen] = useState(false);
  const [collectionReportEmailLoading, setCollectionReportEmailLoading] = useState(false);
  const [invoiceEmailLoading, setInvoiceEmailLoading] = useState(false);
  const [invoiceModal, setInvoiceModal] = useState({
    open: false,
    invoice: null,
    loading: false,
    error: '',
  });
  const [taxEditor, setTaxEditor] = useState({ open: false, value: '0', saving: false, error: '' });
  const [preferencesTaxRate, setPreferencesTaxRate] = useState(0);
  const [invoiceHighlightSerial, setInvoiceHighlightSerial] = useState('');
  const [invoicePrintView, setInvoicePrintView] = useState('invoice');
  const [customerLabelModal, setCustomerLabelModal] = useState({
    open: false,
    customer: null,
    addressType: 'billing',
    labelType: 'shipping',
  });
  const invoiceModalRef = useRef(null);
  const [creditMemoModal, setCreditMemoModal] = useState({
    open: false,
    docNo: '',
    transfer: null,
    loading: false,
    error: '',
  });
  const [creditMemoHighlightSerial, setCreditMemoHighlightSerial] = useState('');
  const [receiveDocModal, setReceiveDocModal] = useState({
    open: false,
    docNo: '',
    transfer: null,
    loading: false,
    error: '',
  });

const loadOrdersForCustomer = async (customer) => {
  if (!canViewShipDocs) return;
  if (!customer?.nccustomerid) return;

  setOrders([]);
  setOrdersLoading(true);
  setOrdersError('');
  setOrderDateRange(getDefaultInvoiceDateRange());

  try {
    const res = await api.get(
      `/locations/by-customer/${customer.nccustomerid}`
    );
    setOrders(res.data || []);
  } catch (err) {
    console.error('Failed to load orders:', err);
    setOrdersError('Failed to load ship docs.');
  } finally {
    setOrdersLoading(false);
  }
};

  const loadInvoicesForCustomer = async (customer) => {
  if (!canViewTransfers) return;
  if (!customer?.nccustomerid) return;

  setInvoices([]);
  setInvoicesLoading(true);
  setInvoicesError('');
  setInvoiceDateRange(getDefaultInvoiceDateRange());

  try {
    const res = await api.get(`/invoices/customer/${customer.nccustomerid}`);
    setInvoices(res.data || []);
  } catch (err) {
    console.error('Failed to load invoices:', err);
    setInvoicesError('Failed to load transfers.');
  } finally {
    setInvoicesLoading(false);
  }
};

  const loadRepairOrdersForCustomer = async (customer) => {
    if (!canViewRepairOrders) return;
    if (!customer?.nccustomerid) return;

    setRepairOrders([]);
    setRepairOrdersLoading(true);
    setRepairOrdersError('');

    try {
      const res = await api.get('/repair-orders', {
        params: {
          customerId: customer.nccustomerid,
          status: 'all',
          year: 'all',
        },
      });
      setRepairOrders(res.data || []);
    } catch (err) {
      console.error('Failed to load repair orders:', err);
      setRepairOrdersError('Failed to load repair orders.');
    } finally {
      setRepairOrdersLoading(false);
    }
  };

  const loadVisibleCustomerDetails = (customer) => {
    if (canViewShipDocs) loadOrdersForCustomer(customer);
    if (canViewTransfers) loadInvoicesForCustomer(customer);
    if (canViewRepairOrders) loadRepairOrdersForCustomer(customer);
  };

  const openInvoiceModal = async (invoiceId, options = {}) => {
    setInvoiceHighlightSerial(String(options.highlightSerial || '').trim());
    setInvoicePrintView('invoice');
    setInvoiceModal({ open: true, invoice: null, loading: true, error: '' });

    try {
      const params = options.transactionTypeId ? { transactionTypeId: options.transactionTypeId } : {};
      const res = await api.get(`/invoices/${invoiceId}`, { params });
      const customerEmail = selectedCustomer?.email || collectionReport?.customerEmail || '';
      let invoice = customerEmail && !res.data?.to?.email
        ? {
          ...res.data,
          to: {
            ...(res.data?.to || {}),
            email: customerEmail,
          },
        }
        : res.data;
      const transactionTypeId = Number(options.transactionTypeId || invoice?.transactionTypeId);
      if (transactionTypeId === 5) {
        const documentTitle = getTransferInvoiceTitle({
          ...invoice,
          transactionTypeId,
        });
        invoice = {
          ...invoice,
          transactionTypeId,
          type: documentTitle,
          documentTitle,
        };
      }
      setInvoiceModal({ open: true, invoice, loading: false, error: '' });
    } catch (err) {
      console.error('Failed to load invoice:', err);
      setInvoiceModal({
        open: true,
        invoice: null,
        loading: false,
        error: 'Failed to load invoice.',
      });
    }
  };

  const closeInvoiceModal = () => {
    setInvoiceHighlightSerial('');
    setInvoicePrintView('invoice');
    setTaxEditor({ open: false, value: '0', saving: false, error: '' });
    setInvoiceModal({ open: false, invoice: null, loading: false, error: '' });
  };

  const fetchCustomerInvoiceDocument = async (docNo) => {
    const { data } = await api.get(`/invoices/${encodeURIComponent(docNo)}`, {
      params: { transactionTypeId: 4 },
    });
    const customerEmail = selectedCustomer?.email || collectionReport?.customerEmail || '';
    let invoice = {
      ...data,
      documentTitle: data.documentTitle || 'Invoice',
      transactionTypeId: 4,
    };
    if (customerEmail && !invoice?.to?.email) {
      invoice = {
        ...invoice,
        to: {
          ...(invoice?.to || {}),
          email: customerEmail,
        },
      };
    }
    if (businessAddress && !invoice?.businessAddress) {
      invoice = {
        ...invoice,
        businessAddress,
      };
    }
    return invoice;
  };

  const canEditInvoiceTax = Boolean(
    invoiceModal.invoice
      && Number(invoiceModal.invoice.transactionTypeId) === 4
      && !isRepairInvoiceDocument(invoiceModal.invoice)
  );

  const refreshCustomerShipDocInvoice = async (docNo) => {
    const invoice = await fetchCustomerInvoiceDocument(docNo);
    let mergedInvoice = invoice;
    try {
      const detailRes = await api.get(`/payments/documents/invoice/${encodeURIComponent(docNo)}`, {
        params: selectedCustomer?.nccustomerid ? { customerId: selectedCustomer.nccustomerid } : {},
      });
      mergedInvoice = mergePaymentDetailIntoInvoiceDocument(invoice, detailRes.data);
    } catch {
      mergedInvoice = invoice;
    }
    setInvoiceModal({ open: true, invoice: mergedInvoice, loading: false, error: '' });
    if (selectedCustomer?.nccustomerid) {
      loadOrdersForCustomer(selectedCustomer);
    }
  };

  const openTaxEditor = () => {
    if (!canEditInvoiceTax) return;
    setTaxEditor({
      open: true,
      value: Number(invoiceModal.invoice?.taxAmount || 0) > 0 && preferencesTaxRate > 0 ? String(preferencesTaxRate) : '0',
      saving: false,
      error: '',
    });
  };

  const closeTaxEditor = () => {
    if (taxEditor.saving) return;
    setTaxEditor({ open: false, value: '0', saving: false, error: '' });
  };

  const saveInvoiceTax = async (event) => {
    event.preventDefault();
    if (!canEditInvoiceTax) return;
    const docNo = invoiceModal.invoice?.docNo || invoiceModal.invoice?.invoiceNo || invoiceModal.invoice?.id;
    if (!docNo) return;
    setTaxEditor((current) => ({ ...current, saving: true, error: '' }));
    try {
      await api.patch(`/invoices/${encodeURIComponent(docNo)}/tax`, {
        taxAmount: taxAmountForRate(invoiceModal.invoice, taxEditor.value),
      });
      await refreshCustomerShipDocInvoice(docNo);
      setTaxEditor({ open: false, value: '0', saving: false, error: '' });
    } catch (err) {
      setTaxEditor((current) => ({
        ...current,
        saving: false,
        error: err.response?.data?.error || err.message || 'Failed to update invoice tax.',
      }));
    }
  };

  const printCustomerInvoiceOutput = () => {
    const invoice = invoiceModal.invoice;
    if (!invoice) return;
    if (isRepairInvoiceDocument(invoice) && invoicePrintView !== 'invoice') {
      printTransferLabel({
        location: invoice.to,
        pickup: invoicePrintView === 'pickupLabel',
        services: true,
        docNo: invoice.invoiceNo,
      });
      return;
    }

    const printClassName = 'invoice-document-printing';
    const cleanupPrintMode = () => {
      document.body.classList.remove(printClassName);
      window.removeEventListener('afterprint', cleanupPrintMode);
    };
    document.body.classList.add(printClassName);
    window.addEventListener('afterprint', cleanupPrintMode, { once: true });
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => window.print());
    });
  };

  const openShipDocInvoice = async (row) => {
    const docNo = row?.docno;
    if (!docNo) return;

    const transactionTypeId = Number(row?.trantypeid);
    const transactionType = String(row?.trantype || '').trim().toLowerCase();
    const isRepairOrder = transactionTypeId === 640 || transactionType.includes('repair');
    const isShipping = transactionTypeId === 4 || transactionType.includes('shipping');
    const isReceiving = transactionTypeId === 2 || transactionType.includes('receiving');

    if (isRepairOrder) {
      openInvoiceModal(`RO-${docNo}`, { transactionTypeId: 640 });
      return;
    }

    if (isShipping) {
      setInvoicePrintView('invoice');
      setInvoiceModal({ open: true, invoice: null, loading: true, error: '' });
      try {
        const invoice = await fetchCustomerInvoiceDocument(docNo);
        let mergedInvoice = invoice;
        try {
          const detailRes = await api.get(`/payments/documents/invoice/${encodeURIComponent(docNo)}`, {
            params: selectedCustomer?.nccustomerid ? { customerId: selectedCustomer.nccustomerid } : {},
          });
          mergedInvoice = mergePaymentDetailIntoInvoiceDocument(invoice, detailRes.data);
        } catch {
          mergedInvoice = invoice;
        }
        setInvoiceModal({ open: true, invoice: mergedInvoice, loading: false, error: '' });
      } catch (err) {
        try {
          const customerEmail = selectedCustomer?.email || collectionReport?.customerEmail || '';
          const detailRes = await api.get(`/payments/documents/invoice/${encodeURIComponent(docNo)}`, {
            params: selectedCustomer?.nccustomerid ? { customerId: selectedCustomer.nccustomerid } : {},
          });
          const invoice = paymentDetailToInvoiceDocument({
            documentId: docNo,
            detail: detailRes.data,
            customer: selectedCustomer,
            businessAddress,
            fallbackEmail: customerEmail,
          });
          setInvoiceModal({ open: true, invoice, loading: false, error: '' });
        } catch (detailErr) {
          console.error('Failed to load shipping invoice:', err, detailErr);
          setInvoiceModal({
            open: true,
            invoice: null,
            loading: false,
            error: 'Failed to load shipping invoice.',
          });
        }
      }
      return;
    }

    if (isReceiving || !Number.isInteger(transactionTypeId)) {
      openReceiveDocModal(docNo);
      return;
    }

    openInvoiceModal(docNo, {
      transactionTypeId,
    });
  };

  useEffect(() => {
    if (!invoiceModal.open) return undefined;

    const frame = window.requestAnimationFrame(() => {
      if (invoiceModalRef.current) {
        invoiceModalRef.current.scrollTop = 0;
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [invoiceModal.open, invoiceModal.invoice, invoiceModal.loading]);

  const openCollectionReportDocument = async (row) => {
    const documentId = row?.documentId || row?.invoiceNo;
    if (!documentId) return;

    if (Number(row.transactionTypeId) === 10) {
      setCreditMemoHighlightSerial(String(row.serialNo || '').trim());
      setCreditMemoModal({ open: true, docNo: documentId, transfer: null, loading: true, error: '' });
      try {
        const res = await api.get(`/transfers/${documentId}`, {
          params: { transactionTypeId: 10 },
        });
        setCreditMemoModal({
          open: true,
          docNo: documentId,
          transfer: res.data,
          loading: false,
          error: '',
        });
      } catch (err) {
        console.error('Failed to load credit memo:', err);
        setCreditMemoModal({
          open: true,
          docNo: documentId,
          transfer: null,
          loading: false,
          error: 'Failed to load credit memo.',
        });
      }
      return;
    }

    openInvoiceModal(documentId, {
      transactionTypeId: row.transactionTypeId,
      highlightSerial: row.serialNo,
    });
  };

  const closeCreditMemoModal = () => {
    setCreditMemoHighlightSerial('');
    setCreditMemoModal({ open: false, docNo: '', transfer: null, loading: false, error: '' });
  };

  const printCreditMemoDocument = () => {
    const source = document.querySelector('.customer-credit-memo-modal .shipdoc-invoice-workspace');
    if (!source) return;

    const printNode = source.cloneNode(true);
    printNode.querySelectorAll('.shipdoc-invoice-actions, .transfer-print-button').forEach((node) => node.remove());

    const iframe = document.createElement('iframe');
    iframe.title = 'Credit memo print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '850px';
    iframe.style.height = '1100px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!doctype html>
      <html>
        <head>
          <title>Credit Memo ${escapeHtml(creditMemoModal.transfer?.docNo || creditMemoModal.docNo || '')}</title>
          <style>
            @page { size: portrait; margin: 12mm; }
            * { box-sizing: border-box; }
            body {
              background: #fff;
              color: #000;
              font-family: "Times New Roman", Times, serif;
              margin: 0;
            }
            .shipdoc-invoice-workspace { background: #fff; padding: 0; }
            .shipdoc-invoice-actions { display: none; }
            .shipdoc-invoice-sheet {
              background: #fff;
              box-shadow: none;
              color: #000;
              font-family: "Times New Roman", Times, serif;
              margin: 0;
              min-height: auto;
              padding: 50px 28px 40px;
              position: relative;
              width: 100%;
            }
            .shipdoc-paid-watermark { align-items: center; color: rgba(0, 0, 0, 0.72); display: flex; flex-direction: column; font-family: "Courier New", Courier, monospace; font-weight: 900; left: 50%; letter-spacing: 0; line-height: 0.92; pointer-events: none; position: absolute; text-align: center; text-transform: uppercase; top: 178px; transform: translateX(-50%) rotate(-18deg); z-index: 2; }
            .shipdoc-paid-watermark strong { border: 5px double currentColor; font-size: 44px; line-height: 0.9; padding: 3px 10px 0; }
            .shipdoc-paid-watermark span { font-size: 23px; margin-top: 2px; }
            .shipdoc-invoice-top {
              align-items: start;
              display: grid;
              grid-template-columns: 1fr 230px;
              margin: 0 auto 28px;
              width: 78%;
            }
            .shipdoc-company {
              font-size: 16px;
              line-height: 1.12;
              padding-top: 4px;
            }
            .shipdoc-title-block h1 {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 30px;
              font-weight: 700;
              letter-spacing: 0;
              margin: 0 0 8px;
              text-align: right;
            }
            .shipdoc-invoice-meta {
              border-collapse: collapse;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12px;
              margin-left: auto;
              table-layout: fixed;
              width: 190px;
            }
            .shipdoc-invoice-meta th,
            .shipdoc-invoice-meta td {
              border: 2px solid #000;
              height: 30px;
              padding: 5px 7px;
              text-align: center;
            }
            .shipdoc-invoice-meta th {
              background: #fff;
              font-weight: 400;
            }
            .shipdoc-bill-to-box {
              border: 2px solid #000;
              margin: 0 0 34px 34px;
              min-height: 145px;
              width: 360px;
            }
            .shipdoc-bill-to-box h2 {
              border-bottom: 2px solid #000;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12px;
              font-weight: 400;
              margin: 0;
              padding: 8px 16px;
            }
            .shipdoc-bill-to {
              font-size: 12px;
              line-height: 1.15;
              min-height: 0;
              padding: 5px 6px;
            }
            .shipdoc-lines-area {
              margin-top: 8px;
              position: relative;
            }
            .shipdoc-detail-strip {
              border-collapse: collapse;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 12px;
              margin-left: auto;
              margin-right: 0;
              table-layout: fixed;
              width: 48.5%;
            }
            .shipdoc-detail-strip th,
            .shipdoc-detail-strip td {
              border: 2px solid #000;
              height: 30px;
              padding: 6px;
              text-align: center;
            }
            .shipdoc-detail-strip th {
              background: #fff;
              font-weight: 400;
            }
            .shipdoc-invoice-items {
              border-collapse: collapse;
              font-size: 12px;
              margin-top: -2px;
              table-layout: fixed;
              width: 100%;
            }
            .shipdoc-invoice-items th {
              background: #fff;
              border: 2px solid #000;
              font-family: Arial, Helvetica, sans-serif;
              font-weight: 400;
              height: 32px;
              padding: 6px;
              text-align: center;
            }
            .shipdoc-invoice-items td {
              border-left: 2px solid #000;
              border-right: 2px solid #000;
              line-height: 1.12;
              padding: 2px 6px;
              text-align: left;
              vertical-align: top;
            }
            .shipdoc-invoice-items tbody tr:first-child td { padding-top: 5px; }
            .shipdoc-invoice-items tbody tr:last-child td { padding-bottom: 260px; }
            .shipdoc-invoice-items th:nth-child(1),
            .shipdoc-invoice-items td:nth-child(1) { width: 30%; }
            .shipdoc-invoice-items th:nth-child(2),
            .shipdoc-invoice-items td:nth-child(2) { width: 35%; }
            .shipdoc-invoice-items th:nth-child(3),
            .shipdoc-invoice-items td:nth-child(3) {
              text-align: right;
              width: 19%;
            }
            .shipdoc-invoice-items th:nth-child(4),
            .shipdoc-invoice-items td:nth-child(4) {
              text-align: right;
              width: 16%;
            }
            .shipdoc-highlight-row td { background: #fff3bf; }
            .shipdoc-summary-row {
              display: grid;
              grid-template-columns: 1fr 35%;
              margin-top: -2px;
            }
            .shipdoc-summary-spacer {
              border: 2px solid #000;
              border-right: 0;
              min-height: 38px;
            }
            .shipdoc-summary {
              border-bottom: 2px solid #000;
              border-right: 2px solid #000;
              box-sizing: border-box;
              margin: 0;
              min-width: 0;
              width: auto;
            }
            .shipdoc-summary div {
              align-items: center;
              border-left: 2px solid #000;
              border-top: 2px solid #000;
              box-sizing: border-box;
              display: grid;
              grid-template-columns: minmax(0, 1fr) 104px;
              min-height: 38px;
              overflow: hidden;
            }
            .shipdoc-summary span {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 18px;
              font-weight: 700;
              min-width: 0;
              padding-left: 16px;
              white-space: nowrap;
            }
            .shipdoc-summary strong {
              display: block;
              font-size: 12px;
              font-weight: 400;
              min-width: 0;
              padding-right: 10px;
              text-align: right;
              white-space: nowrap;
            }
            .shipdoc-summary div:last-child span { font-size: 21px; }
          </style>
        </head>
        <body>${printNode.outerHTML}</body>
      </html>`);
    doc.close();

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };
    setTimeout(runPrint, 100);
  };

  const openReceiveDocModal = async (docNo) => {
    if (!docNo) return;
    setReceiveDocModal({ open: true, docNo, transfer: null, loading: true, error: '' });

    try {
      const res = await api.get(`/transfers/${docNo}`, {
        params: { transactionTypeId: 2 },
      });
      setReceiveDocModal({ open: true, docNo, transfer: res.data, loading: false, error: '' });
    } catch (err) {
      console.error('Failed to load receive doc:', err);
      setReceiveDocModal({
        open: true,
        docNo,
        transfer: null,
        loading: false,
        error: 'Failed to load receive doc.',
      });
    }
  };

  const closeReceiveDocModal = () => {
    setReceiveDocModal({ open: false, docNo: '', transfer: null, loading: false, error: '' });
  };

  const loadCollectionReportForCustomer = useCallback(async (customer) => {
    if (!canViewCustomerProfile) return;
    if (!customer?.nccustomerid) return;

    setCollectionReport(null);
    setCollectionReportSort({ key: 'date', direction: 'asc' });
    setCollectionItemFilter('');
    setCollectionSerialFilter('');
    setCollectionItemFilterOpen(false);
    setCollectionReportLoading(true);
    setCollectionReportError('');

    try {
      const res = await api.get(`/customers/${customer.nccustomerid}/collection-report`);
      setCollectionReport(res.data ? {
        ...res.data,
        customerEmail: res.data.customerEmail || customer.email || '',
      } : null);
    } catch (err) {
      console.error('Failed to load collection report:', err);
      setCollectionReportError('Failed to load collection report.');
    } finally {
      setCollectionReportLoading(false);
    }
  }, [canViewCustomerProfile]);


  const firstVisibleCustomerTab = canViewShipDocs
    ? 'orders'
    : canViewTransfers
      ? 'invoices'
      : canViewRepairOrders
        ? 'repairOrders'
        : 'profile';

  // NEW: tabs
  const [activeTab, setActiveTab] = useState(firstVisibleCustomerTab);
  const [lastSelectedCustomerId, setLastSelectedCustomerId] = useState(null);

  const [isCustomerTypeFilterOpen, setIsCustomerTypeFilterOpen] = useState(false);
  const [selectedCustomerTypeIds, setSelectedCustomerTypeIds] = useState([]);
  const [form, setForm] = useState({ name: '' });
  const [isCustomerTypeaheadOpen, setIsCustomerTypeaheadOpen] = useState(false);
  const [activeCustomerTypeaheadIndex, setActiveCustomerTypeaheadIndex] = useState(0);
  const customerTypeaheadMenuRef = useRef(null);

  const [deleteCustomerConfirm, setDeleteCustomerConfirm] = useState({
    open: false,
    customer: null,
    value: '',
    error: '',
  });
  const [deleteCustomerLoading, setDeleteCustomerLoading] = useState(false);

  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
  });

  const showAlert = (message) => setAlertState({ open: true, message });

  useEffect(() => {
    if (!linkedCustomerId || !['collectionReport', 'crm'].includes(linkedTab) || !canViewCustomerProfile) return undefined;
    let cancelled = false;
    api.get(`/customers/${encodeURIComponent(linkedCustomerId)}`)
      .then(({ data }) => {
        if (cancelled) return;
        const customer = normalizeCustomerProfile(data);
        setSelectedCustomer(customer);
        setUpdateCustomer({ ...customer });
        setLastSelectedCustomerId(customer.id);
        setForm((current) => ({ ...current, name: customer.name || '' }));
        setFilteredCustomers([customer]);
        setActiveTab(linkedTab === 'crm' && can(user, 'customers.crm', customer) ? 'crm' : 'collectionReport');
        if (linkedTab === 'collectionReport') loadCollectionReportForCustomer(customer);
      })
      .catch(() => {
        if (!cancelled) setAlertState({ open: true, message: 'Failed to load the selected customer.' });
      });
    return () => { cancelled = true; };
  }, [linkedCustomerId, linkedTab, canViewCustomerProfile, loadCollectionReportForCustomer, user, crmNavigationKey]);

  useEffect(() => {
    const visibleTabs = [
      canViewShipDocs && 'orders',
      canViewTransfers && 'invoices',
      canViewRepairOrders && 'repairOrders',
      canViewCustomerProfile && 'collectionReport',
      canViewCustomerProfile && 'profile',
      canViewCustomerCrm && 'crm',
    ].filter(Boolean);
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(firstVisibleCustomerTab);
    }
  }, [
    activeTab,
    canViewCustomerProfile,
    canViewCustomerCrm,
    canViewRepairOrders,
    canViewShipDocs,
    canViewTransfers,
    firstVisibleCustomerTab,
  ]);

  const closeAddCustomerModal = () => {
    setIsAddCustomerOpen(false);
    setCustomerAddStatus('');
    setDuplicatePhoneCustomers([]);
    setNewEmployee({
      companyname: '',
      customertypeid: getDefaultCustomerTypeId(creatableCustomerTypes),
      phone: '',
      email: '',
    });
  };

  useEffect(() => {
    document.title = "Customers - Services Irene's Wigs.com";
    loadCustomers();
    loadCustomerTypes();
    api.get('/preferences')
      .then((res) => setPreferencesTaxRate(Number(res.data?.taxRate || 0)))
      .catch((err) => console.error('Failed to load preferences:', err));
  }, []);

  const loadCustomers = async () => {
    try {
      const res = await api.get('/customers');
      const rows = (res.data || []).map(normalizeCustomerProfile);
      setCustomers(rows);
      return rows;
    } catch (err) {
      console.error('Failed to load customers:', err);
      showAlert('Failed to load customers.');
      return [];
    }
  };

  const loadCustomerTypes = async () => {
    try {
      const res = await api.get('/customers/types');
      const types = res.data || [];
      setCustomerTypes(types);
      setSelectedCustomerTypeIds((current) => (
        current.length > 0 ? current : types.map((type) => String(type.id))
      ));
      setNewEmployee((current) => ({
        ...current,
        customertypeid: current.customertypeid || getDefaultCustomerTypeId(creatableCustomerTypes.length ? creatableCustomerTypes : types),
      }));
    } catch (err) {
      console.error('Failed to load customer types:', err);
      showAlert('Failed to load customer types.');
    }
  };

  const handleEmployeeClick = (e, customer) => {
  if (
    e.target.tagName === 'SELECT' ||
    e.target.tagName === 'INPUT' ||
    e.target.tagName === 'TEXTAREA' ||
    e.target.closest('select') ||
    e.target.closest('input') ||
    e.target.closest('button')
  ) {
    return;
  }

  const normalizedCustomer = normalizeCustomerProfile(customer);
  const isCurrentCustomer = selectedCustomer?.id === normalizedCustomer.id;

  if (isCurrentCustomer) {
    setSelectedCustomer(null);
    setUpdateCustomer(null);
    setLastSelectedCustomerId(normalizedCustomer.id);
    return;
  }

  const isReturningToSameCustomer = lastSelectedCustomerId === normalizedCustomer.id;

  setSelectedCustomer(normalizedCustomer);
  setUpdateCustomer({ ...normalizedCustomer });
  setLastSelectedCustomerId(normalizedCustomer.id);

  if (isReturningToSameCustomer) {
    return;
  }

  setActiveTab(firstVisibleCustomerTab);
  loadVisibleCustomerDetails(normalizedCustomer);
  setCollectionReport(null);
};

  const handleSave = async () => {
    if (!updateCustomer || !selectedCustomer) return;
    if (!canEditSelectedCustomer) {
      showAlert('You do not have permission to edit this customer.');
      return;
    }

    try {
      const updatePayload = buildCustomerProfileUpdatePayload(selectedCustomer, updateCustomer);
      if (Object.keys(updatePayload).length === 0) {
        showAlert('No profile changes to save.');
        return;
      }

      const saveRes = await api.put(`/customers/${selectedCustomer.id}`, updatePayload);
      showAlert('Customer updated.');
      const rows = await loadCustomers();
      const savedCustomer = saveRes.data?.customer
        ? normalizeCustomerProfile(saveRes.data.customer)
        : null;
      const refreshedCustomer = rows.find((customer) => customer.id === selectedCustomer.id) || savedCustomer;
      if (refreshedCustomer) {
        const normalizedCustomer = normalizeCustomerProfile(refreshedCustomer);
        setSelectedCustomer(normalizedCustomer);
        setUpdateCustomer({ ...normalizedCustomer });
        setFilteredCustomers((current) => {
          if (current === null) return current;
          return current.map((customer) => (
            String(customer.id) === String(normalizedCustomer.id) ? normalizedCustomer : customer
          ));
        });
      }
    } catch (err) {
      console.error('Failed to update customer:', err);
      showAlert('Failed to update customer.');
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setDuplicatePhoneCustomers([]);
    if (!canCreateCustomer) {
      setCustomerAddStatus('You do not have permission to add customers.');
      return;
    }
    const selectedType = creatableCustomerTypes.find((type) => (
      String(type.id) === String(newEmployee.customertypeid || getDefaultCustomerTypeId(creatableCustomerTypes))
    ));
    if (!selectedType) {
      setCustomerAddStatus('You can only add customers of an allowed customer type.');
      return;
    }
    setCustomerAddStatus('');
    try {
      const payload = {
        name: newEmployee.companyname?.trim(),
        companyname: newEmployee.companyname?.trim(),
        customertypeid: selectedType.id,
        phone: newEmployee.phone?.trim(),
        email: newEmployee.email?.trim(),
      };

      const res = await api.post('/customers', payload);
      if (res.status === 201) {
        setCustomerAddStatus('✅ Customer added!');
        const createdCustomerId = res.data?.customer?.id ?? res.data?.id;
        setNewEmployee({
          companyname: '',
          customertypeid: getDefaultCustomerTypeId(creatableCustomerTypes),
          phone: '',
          email: '',
        });
        setIsAddCustomerOpen(false);
        showAlert('Customer added.');
        const rows = await loadCustomers();
        const createdCustomer = rows.find((customer) => String(customer.id) === String(createdCustomerId))
          || normalizeCustomerProfile(res.data?.customer);
        if (createdCustomer?.id) {
          const normalizedCustomer = normalizeCustomerProfile(createdCustomer);
          setSelectedCustomer(normalizedCustomer);
          setUpdateCustomer({ ...normalizedCustomer });
          setLastSelectedCustomerId(normalizedCustomer.id);
          setActiveTab('profile');
          setFilteredCustomers([normalizedCustomer]);
          setForm((prev) => ({ ...prev, name: normalizedCustomer.name || '' }));
          setIsCustomerTypeaheadOpen(false);
          setActiveCustomerTypeaheadIndex(0);
          setCollectionReport(null);
          setOrders([]);
          setInvoices([]);
        } else {
          setFilteredCustomers(null);
        }
      } else {
        setCustomerAddStatus(res.data?.error || 'Failed to add customer.');
      }
    } catch (err) {
      setCustomerAddStatus(err.response?.data?.error || err.message);
      if (err.response?.data?.code === 'DUPLICATE_CUSTOMER_PHONE') {
        setDuplicatePhoneCustomers(err.response.data.customers || []);
      }
    }
  };

  const handleDelete = async (id) => {
    const employeeToDelete = employees.find((u) => u.id === id)
      || (selectedCustomer?.id === id ? selectedCustomer : null);
    if (!employeeToDelete) return;
    if (!can(user, 'customers.delete', employeeToDelete)) {
      showAlert('You do not have permission to delete customers.');
      return;
    }

    setDeleteCustomerConfirm({
      open: true,
      customer: employeeToDelete,
      value: '',
      error: '',
    });
  };

  const closeDeleteCustomerConfirm = () => {
    if (deleteCustomerLoading) return;
    setDeleteCustomerConfirm({
      open: false,
      customer: null,
      value: '',
      error: '',
    });
  };

  const deleteCustomer = async (e) => {
    e.preventDefault();
    if (!can(user, 'customers.delete', deleteCustomerConfirm.customer)) {
      setDeleteCustomerConfirm((current) => ({
        ...current,
        error: 'You do not have permission to delete customers.',
      }));
      return;
    }
    const customer = deleteCustomerConfirm.customer;
    if (!customer) return;

    const expectedName = String(customer.name || '').trim();
    const enteredName = String(deleteCustomerConfirm.value || '').trim();

    if (enteredName !== expectedName) {
      setDeleteCustomerConfirm((current) => ({
        ...current,
        error: `Customer name must match ${expectedName}.`,
      }));
      return;
    }

    setDeleteCustomerLoading(true);
    try {
      await api.delete(`/customers/${customer.id}`);
      setCustomers((current) => current.filter((row) => String(row.id) !== String(customer.id)));
      clearFilter();
      setLastSelectedCustomerId(null);
      setActiveTab(firstVisibleCustomerTab);
      setOrders([]);
      setInvoices([]);
      setRepairOrders([]);
      setCollectionReport(null);
      if (linkedCustomerId === String(customer.id)) {
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.delete('customerId');
          next.delete('tab');
          return next;
        }, { replace: true });
      }
      setDeleteCustomerConfirm({
        open: false,
        customer: null,
        value: '',
        error: '',
      });
      await loadCustomers();
      showAlert('Customer deleted.');
    } catch (err) {
      console.error('Error deleting customer:', err);
      setDeleteCustomerConfirm((current) => ({
        ...current,
        error: err.response?.data?.message || err.response?.data?.error || 'Failed to delete customer.',
      }));
    } finally {
      setDeleteCustomerLoading(false);
    }
  };

  const handleNameChange = (e) => {
    setForm((prev) => ({ ...prev, name: e.target.value }));
    setIsCustomerTypeaheadOpen(true);
    setActiveCustomerTypeaheadIndex(0);
  };

  const clearFilter = () => {
    setFilteredCustomers(null);
    setSelectedCustomer(null);
    setUpdateCustomer(null);
    setForm((prev) => ({ ...prev, name: '' }));
    setSelectedCustomerTypeIds(customerTypes.map((type) => String(type.id)));
    setIsCustomerTypeaheadOpen(false);
    setActiveCustomerTypeaheadIndex(0);
  };

  const selectCustomerTypeaheadOption = (customer) => {
    const normalizedCustomer = normalizeCustomerProfile(customer);
    setForm((prev) => ({ ...prev, name: normalizedCustomer.name || '' }));
    setSelectedCustomer(normalizedCustomer);
    setUpdateCustomer({ ...normalizedCustomer });
    setActiveTab(firstVisibleCustomerTab);
    loadVisibleCustomerDetails(normalizedCustomer);
    setFilteredCustomers([normalizedCustomer]);
    setIsCustomerTypeaheadOpen(false);
    setActiveCustomerTypeaheadIndex(0);
  };

  const reapplyFilter = (list, searchText) => {
    const q = String(searchText || '').trim().toLowerCase();
    if (!q) {
      setFilteredCustomers(list);
      return;
    }

    const exact = list.find((u) => (u.name || '').toLowerCase() === q);
    if (exact) {
      const normalizedCustomer = normalizeCustomerProfile(exact);
      setSelectedCustomer(normalizedCustomer);
      setUpdateCustomer({ ...normalizedCustomer });
      setActiveTab(firstVisibleCustomerTab);
      loadVisibleCustomerDetails(normalizedCustomer);
      setFilteredCustomers([normalizedCustomer]);
      return;
    }

    const partials = list.filter((u) => customerMatchesSearch(u, q));
    if (partials.length === 1) {
      const normalizedCustomer = normalizeCustomerProfile(partials[0]);
      setSelectedCustomer(normalizedCustomer);
      setUpdateCustomer({ ...normalizedCustomer });
      setActiveTab(firstVisibleCustomerTab);
      loadVisibleCustomerDetails(normalizedCustomer);
      setFilteredCustomers([normalizedCustomer]);
      return;
    }
    if (partials.length > 1) {
      setSelectedCustomer(null);
      setUpdateCustomer(null);
      setFilteredCustomers(partials);
      return;
    }
    setSelectedCustomer(null);
    setUpdateCustomer(null);
    setFilteredCustomers([]);
    showAlert('No customer found with that search.');
  };

  const findCustomer = () => {
    const customerSearch = form.name?.trim();
    if (!customerSearch) return showAlert('Please enter a name or phone number to search.');
    reapplyFilter(employees, customerSearch);
  };

  let listToRender = filteredCustomers === null ? employees : filteredCustomers;
  if (customerTypes.length > 0) {
    const selectedTypeSet = new Set(selectedCustomerTypeIds.map(String));
    const knownTypeSet = new Set(customerTypes.map((type) => String(type.id)));
    const allKnownTypesSelected = customerTypes.every((type) => selectedTypeSet.has(String(type.id)));
    listToRender = listToRender.filter((u) => {
      const customerTypeId = String(u.type || '');
      const isExplicitSelection = selectedCustomer?.id === u.id;
      const hasUnassignedOrLegacyType = !customerTypeId || !knownTypeSet.has(customerTypeId);
      return isExplicitSelection
        || selectedTypeSet.has(customerTypeId)
        || (allKnownTypesSelected && hasUnassignedOrLegacyType);
    });
  }

  const toggleCustomerTypeFilter = (typeId) => {
    const idText = String(typeId);
    setSelectedCustomerTypeIds((current) => (
      current.includes(idText)
        ? current.filter((id) => id !== idText)
        : [...current, idText]
    ));
  };

  const customerTypeaheadQuery = (form.name || '').trim().toLowerCase();
  const customerTypeaheadOptions = employees
    .filter((customer) => customerMatchesSearch(customer, customerTypeaheadQuery))
    .slice(0, 30);

  useEffect(() => {
    if (!isCustomerTypeaheadOpen) return;
    const menu = customerTypeaheadMenuRef.current;
    const activeOption = menu?.children?.[activeCustomerTypeaheadIndex];
    if (!menu || !activeOption) return;

    const optionTop = activeOption.offsetTop;
    const optionBottom = optionTop + activeOption.offsetHeight;
    const visibleTop = menu.scrollTop;
    const visibleBottom = visibleTop + menu.clientHeight;

    if (optionTop < visibleTop) {
      menu.scrollTop = optionTop;
    } else if (optionBottom > visibleBottom) {
      menu.scrollTop = optionBottom - menu.clientHeight;
    }
  }, [activeCustomerTypeaheadIndex, isCustomerTypeaheadOpen]);

  const handleCustomerTypeaheadKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsCustomerTypeaheadOpen(true);
      setActiveCustomerTypeaheadIndex((current) => (
        customerTypeaheadOptions.length
          ? Math.min(current + 1, customerTypeaheadOptions.length - 1)
          : 0
      ));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIsCustomerTypeaheadOpen(true);
      setActiveCustomerTypeaheadIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsCustomerTypeaheadOpen(false);
      setActiveCustomerTypeaheadIndex(0);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (isCustomerTypeaheadOpen && customerTypeaheadOptions[activeCustomerTypeaheadIndex]) {
        const customer = customerTypeaheadOptions[activeCustomerTypeaheadIndex];
        const customerName = customer.name || '';
        setForm((prev) => ({ ...prev, name: customerName }));
        reapplyFilter(employees, customerName);
        setIsCustomerTypeaheadOpen(false);
        setActiveCustomerTypeaheadIndex(0);
        return;
      }
      findCustomer();
      setIsCustomerTypeaheadOpen(false);
      return;
    }

    if (e.key === 'Tab' && isCustomerTypeaheadOpen && customerTypeaheadOptions[activeCustomerTypeaheadIndex]) {
      selectCustomerTypeaheadOption(customerTypeaheadOptions[activeCustomerTypeaheadIndex]);
    }
  };

  const formatMoney = (value) => {
    if (value == null || value === '') return '—';
    const number = Number(String(value).replace(/[$,]/g, ''));
    if (!Number.isFinite(number)) return '—';
    return number.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
    });
  };

  const collectionItemFilterText = collectionItemFilter.trim().toLowerCase();
  const collectionSerialFilterText = collectionSerialFilter.trim().toLowerCase();
  const collectionItemOptions = Array.from(new Set(
    (collectionReport?.rows || [])
      .map((row) => String(row.itemService || '').trim())
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const collectionItemSuggestions = collectionItemOptions
    .filter((item) => !collectionItemFilterText || item.toLowerCase().includes(collectionItemFilterText))
    .slice(0, 20);
  const filteredCollectionRows = (collectionReport?.rows || []).filter((row) => (
    (
      !collectionItemFilterText
      || String(row.itemService || '').toLowerCase().includes(collectionItemFilterText)
    )
    && (
      !collectionSerialFilterText
      || String(row.serialNo || '').toLowerCase().includes(collectionSerialFilterText)
    )
  ));
  const sortedCollectionRows = [...filteredCollectionRows].sort((a, b) => {
    let comparison = 0;
    if (collectionReportSort.key === 'date') {
      comparison = dateSortValue(a.invoiceDate) - dateSortValue(b.invoiceDate);
    } else {
      const valueA = collectionReportSort.key === 'serial' ? a.serialNo : a.itemService;
      const valueB = collectionReportSort.key === 'serial' ? b.serialNo : b.itemService;
      comparison = String(valueA || '').localeCompare(String(valueB || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    }
    return collectionReportSort.direction === 'desc' ? -comparison : comparison;
  });
  const displayedCollectionTotalQuantity = sortedCollectionRows.reduce(
    (sum, row) => sum + (Number(row.quantity) || 0),
    0
  );
  const displayedCollectionTotalAmount = sortedCollectionRows.reduce(
    (sum, row) => sum + (Number(row.total) || 0),
    0
  );
  const displayedCollectionReport = collectionReport ? {
    ...collectionReport,
    rows: sortedCollectionRows,
    totalQuantity: displayedCollectionTotalQuantity,
    totalAmount: displayedCollectionTotalAmount,
  } : null;

  const sortCollectionReportBy = (key) => {
    setCollectionReportSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const collectionReportSortArrow = (key) => (
    collectionReportSort.key === key && collectionReportSort.direction === 'asc' ? 'asc' : 'desc'
  );

  const sortInvoicesByParty = (key) => {
    setInvoicesSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const invoiceSortValue = (invoice, key) => {
    const value = key === 'from' ? invoice.fromName : invoice.toName;
    return String(value || '').trim().toLowerCase();
  };

  const invoiceSortArrow = (key) => (
    invoicesSort.key === key && invoicesSort.direction === 'asc' ? 'asc' : 'desc'
  );

  const invoiceDateKey = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  };

  const filteredInvoicesByDate = invoices.filter((invoice) => {
    const hasDateFilter = Boolean(invoiceDateRange.start || invoiceDateRange.end);
    const dateKey = invoiceDateKey(invoice.date);
    if (!hasDateFilter) return true;
    if (!dateKey) return false;
    if (invoiceDateRange.start && dateKey < invoiceDateRange.start) return false;
    if (invoiceDateRange.end && dateKey > invoiceDateRange.end) return false;
    return true;
  });

  const filteredOrdersByDate = orders.filter((order) => {
    const hasDateFilter = Boolean(orderDateRange.start || orderDateRange.end);
    const dateKey = invoiceDateKey(order.datecreated || order.entrydate);
    const matchesInvoice = !shipDocInvoiceFilter.trim()
      || String(order.docno || '').toLowerCase().includes(shipDocInvoiceFilter.trim().toLowerCase());
    if (!matchesInvoice) return false;
    if (!hasDateFilter) return true;
    if (!dateKey) return false;
    if (orderDateRange.start && dateKey < orderDateRange.start) return false;
    if (orderDateRange.end && dateKey > orderDateRange.end) return false;
    return true;
  }).sort((a, b) => {
    const comparison = String(a.docno || '').localeCompare(String(b.docno || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    return shipDocInvoiceSortDirection === 'asc' ? comparison : -comparison;
  });

  const sortedInvoices = invoicesSort.key
    ? filteredInvoicesByDate
        .map((invoice, index) => ({ invoice, index }))
        .sort((a, b) => {
          const valueA = invoiceSortValue(a.invoice, invoicesSort.key);
          const valueB = invoiceSortValue(b.invoice, invoicesSort.key);
          if (!valueA && valueB) return 1;
          if (valueA && !valueB) return -1;
          const comparison = valueA.localeCompare(valueB, undefined, { numeric: true });
          if (comparison !== 0) {
            return invoicesSort.direction === 'asc' ? comparison : -comparison;
          }
          return a.index - b.index;
        })
        .map(({ invoice }) => invoice)
    : filteredInvoicesByDate;

  const hasInvoiceDateFilter = Boolean(
    invoiceDateRange.start || invoiceDateRange.end !== getTodayDateInputValue()
  );

  const hasOrderDateFilter = Boolean(
    orderDateRange.start || orderDateRange.end !== getTodayDateInputValue()
  );

  const collectionReportCustomerName =
    collectionReport?.customerName ||
    selectedCustomer?.name ||
    [selectedCustomer?.firstname, selectedCustomer?.lastname].filter(Boolean).join(' ') ||
    selectedCustomer?.companyname ||
    'Customer';

  const collectionDocumentLabel = (row) => (
    Number(row?.transactionTypeId) === 10
      ? `#${row?.invoiceNo || row?.documentId || ''}`
      : (row?.invoiceNo || '')
  );

  const handlePrintCollectionReport = () => {
    const pageCapacity = 60;
    const rowPages = [];
    let currentPage = [];
    let currentPageUnits = 0;

    sortedCollectionRows.forEach((row) => {
      const descriptionLines = Math.max(1, Math.ceil(String(row.itemService || '').length / 46));
      const repairLines = row.repairNote
        ? Math.max(1, Math.ceil((String(row.repairNote).length + 8) / 46))
        : 0;
      const rowUnits = descriptionLines + repairLines;

      if (currentPage.length > 0 && currentPageUnits + rowUnits > pageCapacity) {
        rowPages.push(currentPage);
        currentPage = [];
        currentPageUnits = 0;
      }

      currentPage.push(row);
      currentPageUnits += rowUnits;
    });
    if (currentPage.length > 0) rowPages.push(currentPage);
    if (rowPages.length === 0) rowPages.push([]);

    const reportDate = formatDate(new Date());
    const businessStreet = [
      businessAddress?.addr1,
      businessAddress?.addr2,
      businessAddress?.addr3,
    ].filter(Boolean).join(', ');
    const businessCity = [
      businessAddress?.city,
      businessAddress?.state,
      businessAddress?.zip,
      businessAddress?.country,
    ].filter(Boolean).join(' ');
    const businessContactLine = [
      [businessStreet, businessCity].filter(Boolean).join(', '),
      businessAddress?.phone ? `TEL: ${businessAddress.phone}` : '',
    ].filter(Boolean).join(' | ');
    const customElectronic = [
      businessAddress?.customPlatform,
      businessAddress?.customAddress,
    ].filter(Boolean).join(': ');
    const businessElectronicLine = [
      businessAddress?.website,
      businessAddress?.email,
      businessAddress?.instagram ? `Instagram: ${businessAddress.instagram}` : '',
      businessAddress?.facebook ? `Facebook: ${businessAddress.facebook}` : '',
      customElectronic,
    ].filter(Boolean).join(' | ');

    const reportPagesHtml = rowPages.map((pageRows, pageIndex) => {
      const isLastPage = pageIndex === rowPages.length - 1;
      const rowsHtml = pageRows.map((row) => {
        const repairNote = row.repairNote
          ? `<div class="repair-note">Repair: ${escapeHtml(row.repairNote)}</div>`
          : '';

        return `
          <tr>
            <td>${escapeHtml(formatDate(row.invoiceDate))}</td>
            <td>${escapeHtml(collectionDocumentLabel(row))}</td>
            <td>${escapeHtml(row.serialNo || '')}</td>
            <td>${escapeHtml(row.itemService || '')}${repairNote}</td>
            <td>${escapeHtml(formatNumber(row.quantity))}</td>
            <td>${escapeHtml(formatMoney(row.pricePer))}</td>
            <td>${escapeHtml(formatMoney(row.total))}</td>
          </tr>
        `;
      }).join('');

      const totalHtml = isLastPage ? `
        <tfoot>
          <tr>
            <td colspan="4">Total</td>
            <td>${escapeHtml(formatNumber(displayedCollectionTotalQuantity))}</td>
            <td></td>
            <td>${escapeHtml(formatMoney(displayedCollectionTotalAmount))}</td>
          </tr>
        </tfoot>
      ` : '';

      return `
        <section class="report-page">
          <header class="report-header">
            <div class="invoice-brand">
              <div>IRENE'S</div>
              <span>nyc</span>
            </div>
            <h1>Irenes Wigs | New York</h1>
            <h2>Collection Report</h2>
            <div class="report-name">${escapeHtml(collectionReportCustomerName)}</div>
          </header>
          <table>
            <thead>
              <tr>
                <th>Invoice Date</th>
                <th>Invoice #</th>
                <th>SN #</th>
                <th>Item/Service</th>
                <th>Qty</th>
                <th>Price Per</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            ${totalHtml}
          </table>
          <footer class="report-footer">
            <span>Report date: ${escapeHtml(reportDate)}</span>
            <span class="report-business">
              <span>${escapeHtml(businessContactLine)}</span>
              <span>${escapeHtml(businessElectronicLine)}</span>
            </span>
            <span>Page ${pageIndex + 1} of ${rowPages.length}</span>
          </footer>
        </section>
      `;
    }).join('');

    const iframe = document.createElement('iframe');
    iframe.title = 'Collection report print preview';
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '8.5in';
    iframe.style.height = '11in';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => iframe.remove(), 250);
    };

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!doctype html>
      <html>
        <head>
          <title>Collection Report - ${escapeHtml(collectionReportCustomerName)}</title>
          <style>
            @page { size: letter portrait; margin: 0; }
            * { box-sizing: border-box; }
            html, body { color: #111; font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; }
            .report-page {
              break-after: page;
              display: flex;
              flex-direction: column;
              height: 11in;
              padding: 0.25in 0.4in 0.25in;
              page-break-after: always;
              width: 8.5in;
            }
            .report-page:last-child { break-after: auto; page-break-after: auto; }
            .report-header { flex: 0 0 auto; text-align: center; }
            .invoice-brand {
              color: #5f6368;
              font-family: Georgia, serif;
              font-size: 21px;
              letter-spacing: 3px;
              line-height: 1;
              margin-bottom: 4px;
              text-align: center;
            }
            .invoice-brand span {
              display: block;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 13px;
              font-weight: 700;
              letter-spacing: 0;
              margin-top: 2px;
            }
            h1 { font-size: 17px; margin: 0; }
            h2 { font-size: 18px; margin: 1px 0 5px; }
            .report-name {
              border: 1.5px solid #111;
              font-size: 12px;
              margin-bottom: 5px;
              padding: 2px 6px;
              text-align: left;
              text-transform: uppercase;
            }
            table { border-collapse: collapse; font-size: 10px; table-layout: fixed; width: 100%; }
            thead { display: table-header-group; }
            tr { break-inside: avoid; page-break-inside: avoid; }
            th, td { line-height: 1.15; padding: 1px 3px; text-align: left; vertical-align: top; }
            th { font-size: 10px; }
            th:nth-child(1), td:nth-child(1) { width: 11%; }
            th:nth-child(2), td:nth-child(2) { width: 10%; }
            th:nth-child(3), td:nth-child(3) { width: 11%; }
            th:nth-child(4), td:nth-child(4) { width: 37%; }
            th:nth-child(5), td:nth-child(5) { width: 7%; text-align: right; }
            th:nth-child(6), td:nth-child(6) { width: 11%; text-align: right; }
            th:nth-child(7), td:nth-child(7) { width: 13%; text-align: right; }
            tfoot td { border-top: 1.5px solid #111; font-weight: 700; padding-top: 4px; }
            .repair-note { font-style: italic; margin-top: 1px; }
            .report-footer {
              align-items: center;
              border-top: 1px solid #777;
              display: grid;
              flex: 0 0 auto;
              font-size: 8.5px;
              gap: 10px;
              grid-template-columns: auto 1fr auto;
              margin-top: auto;
              padding-top: 5px;
            }
            .report-business { display: grid; gap: 2px; text-align: center; }
          </style>
        </head>
        <body>${reportPagesHtml}</body>
      </html>
    `);
    doc.close();

    const runPrint = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
      iframe.contentWindow.print();
      setTimeout(cleanup, 1000);
    };
    setTimeout(runPrint, 100);
  };

  return (
    <>
      <TopNav />
      <div className="inventory-container">
        <div className="customers-page-header">
          <div className="customers-heading-tools">
            <h2>Customers</h2>
            <button
              type="button"
              className="customer-type-filter-button"
              onClick={() => setIsCustomerTypeFilterOpen(true)}
              aria-label="Filter customer types"
              title="Filter customer types"
            >
              <FilterIcon />
            </button>
          </div>
          {canCreateCustomer && (
            <button
              type="button"
              className="button small blue"
              onClick={() => {
                setCustomerAddStatus('');
                setNewEmployee((current) => ({
                  ...current,
                  customertypeid: current.customertypeid || getDefaultCustomerTypeId(creatableCustomerTypes),
                }));
                setIsAddCustomerOpen(true);
              }}
            >
              Add Customer
            </button>
          )}
        </div>

        <div className="form-group">
          <div className="input-button-group customers-search-actions">
            <div className="customer-search-typeahead">
              <input
                type="text"
                placeholder="Search by name or phone"
                value={form.name || ''}
                onChange={handleNameChange}
                onFocus={() => {
                  setIsCustomerTypeaheadOpen(true);
                  setActiveCustomerTypeaheadIndex(0);
                }}
                onBlur={() => setTimeout(() => setIsCustomerTypeaheadOpen(false), 120)}
                onKeyDown={handleCustomerTypeaheadKeyDown}
                className="input"
                autoComplete="off"
                role="combobox"
                aria-expanded={isCustomerTypeaheadOpen}
                aria-controls="customer-typeahead-menu"
              />
              {isCustomerTypeaheadOpen && customerTypeaheadOptions.length > 0 && (
                <div
                  ref={customerTypeaheadMenuRef}
                  className="customer-typeahead-menu"
                  id="customer-typeahead-menu"
                  role="listbox"
                >
                  {customerTypeaheadOptions.map((customer, index) => (
                    <button
                      type="button"
                      key={customer.id}
                      className={index === activeCustomerTypeaheadIndex ? 'active' : ''}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseMove={() => setActiveCustomerTypeaheadIndex(index)}
                      onClick={() => selectCustomerTypeaheadOption(customer)}
                      role="option"
                      aria-selected={index === activeCustomerTypeaheadIndex}
                    >
                      <span>{customer.name || '(No name)'}</span>
                      {(customer.phone || customer.altphone) && <small>{customer.phone || customer.altphone}</small>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={findCustomer} className="button small blue">
              Find
            </button>
            <button type="button" onClick={clearFilter} className="button small yellow">
              Clear
            </button>
          </div>
        </div>

        <div className="card-list employees">
          {listToRender.length === 0 && <div className="empty-state">No Customers to display.</div>}

          {listToRender.map((u) => (
            <div key={u.id} className={`card ${selectedCustomer?.id === u.id ? 'selected' : ''}`}>
              <div onClick={(e) => handleEmployeeClick(e, u)} style={{ cursor: 'pointer' }}>
                <h2>{u.name}</h2>


{selectedCustomer?.id === u.id && updateCustomer && (
  <div
    className="employee-detail-panel"
    onClick={(e) => e.stopPropagation()}
    style={{ marginTop: 10 }}
  >
    {/* TABS (always on top) */}
    <div className="customer-tabs">

      {canViewShipDocs && (
        <button
          type="button"
          className={activeTab === 'orders' ? 'active' : ''}
          onClick={() => {
            setActiveTab('orders');
            loadOrdersForCustomer(selectedCustomer || u);
          }}
        >
          Ship Doc
        </button>
      )}

      {canViewTransfers && (
        <button
          type="button"
          className={activeTab === 'invoices' ? 'active' : ''}
          onClick={() => {
            setActiveTab('invoices');
            loadInvoicesForCustomer(selectedCustomer || u);
          }}
        >
          Transfers
        </button>
      )}

      {canViewRepairOrders && (
        <button
          type="button"
          className={activeTab === 'repairOrders' ? 'active' : ''}
          onClick={() => {
            setActiveTab('repairOrders');
            loadRepairOrdersForCustomer(selectedCustomer || u);
          }}
        >
          Repair Orders
        </button>
      )}

      {canViewCustomerProfile && (
        <button
          type="button"
          className={activeTab === 'collectionReport' ? 'active' : ''}
          onClick={() => {
            setActiveTab('collectionReport');
            loadCollectionReportForCustomer(selectedCustomer || u);
          }}
        >
          Collection Report
        </button>
      )}

      {canViewCustomerProfile && (
        <button
          type="button"
          className={activeTab === 'profile' ? 'active' : ''}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
      )}

      {canViewPayments && (
        <button
          type="button"
          onClick={() => window.open(`/payments?customerId=${encodeURIComponent((selectedCustomer || u).nccustomerid)}`, '_blank', 'noopener,noreferrer')}
        >
          Payments
        </button>
      )}

      {canViewCustomerProfile && (
        <button
          type="button"
          className="customer-label-print-button"
          onClick={() => {
            const customer = updateCustomer || selectedCustomer || u;
            setCustomerLabelModal({
              open: true,
              customer,
              addressType: hasCustomerShippingAddress(customer) ? 'shipping' : 'billing',
              labelType: 'shipping',
            });
          }}
        >
          Print Labels
        </button>
      )}

      {canViewCustomerCrm && <button type="button" className={activeTab === 'crm' ? 'active' : ''} onClick={() => setActiveTab('crm')}>CRM</button>}
    </div>

    {/* PANEL CONTENT (below tabs) */}
    <div className="customer-tab-panel inventory-form">
      {activeTab === 'crm' && canViewCustomerCrm && <CustomerCrm key={selectedCustomer.id} customerId={selectedCustomer.nccustomerid || selectedCustomer.id} focusMessageId={searchParams.get('messageId')} />}
      {activeTab === 'profile' && canViewCustomerProfile && (
        <div className="customer-profile-panel">
          <div className="customer-profile-layout">
            <div className="customer-profile-main-column">
              <div className="customer-profile-address-grid">
                <div className="customer-profile-section">
                  <div className="customer-profile-section-title">Address</div>

                  <label>
                    <span>Customer</span>
                    <input
                      type="text"
                      value={updateCustomer.name || ''}
                      onChange={(e) => {
                        const name = e.target.value;
                        setUpdateCustomer({
                          ...updateCustomer,
                          name,
                          fullname: name,
                          full_name: name,
                          companyname: name,
                        });
                      }}
                    />
                  </label>

                  <label>
                    <span>Billing Address 1</span>
                    <input
                      type="text"
                      value={updateCustomer.billaddr1 || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, billaddr1: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Billing Address 2</span>
                    <input
                      type="text"
                      value={updateCustomer.billaddr2 || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, billaddr2: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Billing City</span>
                    <input
                      type="text"
                      value={updateCustomer.billcity || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, billcity: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Billing State</span>
                    <input
                      type="text"
                      value={updateCustomer.billstate || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, billstate: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Billing Zip</span>
                    <input
                      type="text"
                      value={updateCustomer.billzip || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, billzip: e.target.value })}
                    />
                  </label>
                </div>

                <div className="customer-profile-section">
                  <div className="customer-profile-section-title">Shipping</div>

                  <label>
                    <span>Shipping Address 1</span>
                    <input
                      type="text"
                      value={updateCustomer.shipaddr1 || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, shipaddr1: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Shipping Address 2</span>
                    <input
                      type="text"
                      value={updateCustomer.shipaddr2 || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, shipaddr2: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Shipping City</span>
                    <input
                      type="text"
                      value={updateCustomer.shipcity || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, shipcity: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Shipping State</span>
                    <input
                      type="text"
                      value={updateCustomer.shipstate || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, shipstate: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Shipping Zip</span>
                    <input
                      type="text"
                      value={updateCustomer.shipzip || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, shipzip: e.target.value })}
                    />
                  </label>
                </div>
              </div>

              <div className="customer-profile-separator" />
              <div className="customer-profile-contact-grid">
                <div className="customer-profile-section">
                  <label>
                    <span>Contact</span>
                    <input
                      type="text"
                      value={updateCustomer.contact || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, contact: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Alt. Contact</span>
                    <input
                      type="text"
                      value={updateCustomer.altcontact || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, altcontact: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Phone</span>
                    <input
                      type="text"
                      value={updateCustomer.phone || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, phone: e.target.value })}
                    />
                  </label>
                </div>

                <div className="customer-profile-section">
                  <label>
                    <span>Alt. Phone</span>
                    <input
                      type="text"
                      value={updateCustomer.altphone || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, altphone: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Fax</span>
                    <input
                      type="text"
                      value={updateCustomer.fax || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, fax: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Website</span>
                    <input
                      type="text"
                      value={updateCustomer.website || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, website: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Email</span>
                    <input
                      type="text"
                      value={updateCustomer.email || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, email: e.target.value })}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="customer-profile-limits-column">
              <div className="customer-profile-top-grid">
                <div className="customer-profile-section">
                  <label>
                    <span>Customer Type</span>
                    <select
                      value={updateCustomer.customertypeid ?? ''}
                      onChange={(e) => setUpdateCustomer({
                        ...updateCustomer,
                        customertypeid: e.target.value,
                        customerTypeId: e.target.value,
                        customerTypeName: customerTypes.find((type) => String(type.id) === e.target.value)?.name || '',
                      })}
                    >
                      <option value="">Select Type</option>
                      {customerTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Resale</span>
                    <input
                      type="text"
                      placeholder="Resale number"
                      value={updateCustomer.resale || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, resale: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Notes</span>
                    <textarea
                      placeholder="Customer notes"
                      value={updateCustomer.notes || ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, notes: e.target.value })}
                    />
                  </label>
                </div>

                <div className="customer-profile-section">
                  <label>
                    <span>Consignment Balance Limit</span>
                    <input
                      type="number"
                      step="0.01"
                      value={updateCustomer.consignmentBalanceLimit ?? ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, consignmentBalanceLimit: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Aged Inventory Days Limit</span>
                    <input
                      type="number"
                      step="1"
                      value={updateCustomer.agedInventoryDaysLimit ?? ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, agedInventoryDaysLimit: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Inventory Pieces Limit</span>
                    <input
                      type="number"
                      step="1"
                      value={updateCustomer.inventoryPiecesLimit ?? ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, inventoryPiecesLimit: e.target.value })}
                    />
                  </label>

                  <label>
                    <span>Limit Of Same Items</span>
                    <input
                      type="number"
                      step="1"
                      value={updateCustomer.sameItemsLimit ?? ''}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, sameItemsLimit: e.target.value })}
                    />
                  </label>
                </div>
              </div>

              {canEditSelectedCustomer && (
                <div className="customer-profile-actions">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      style={{ width: 'auto', margin: 0 }}
                      checked={updateCustomer.sendTransferSaleEmail === true}
                      onChange={(e) => setUpdateCustomer({ ...updateCustomer, sendTransferSaleEmail: e.target.checked })}
                    />
                    <span>Send Email Confirmation</span>
                  </label>
                  <button onClick={handleSave} className="inline-button update-button">
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

  
  
 {activeTab === 'orders' && canViewShipDocs && (
  <div className="customer-tab-panel">
    {ordersLoading && <p>Loading ship docs…</p>}

    {!ordersLoading && ordersError && (
      <p style={{ color: 'red' }}>{ordersError}</p>
    )}

    {!ordersLoading && !ordersError && orders.length === 0 && (
      <p style={{ opacity: 0.7 }}>No ship docs found for this customer.</p>
    )}

    {!ordersLoading && !ordersError && orders.length > 0 && (
      <div className="customer-transfer-date-range">
        <label>
          <span>From Date</span>
          <input
            type="date"
            value={orderDateRange.start}
            max={orderDateRange.end || undefined}
            onChange={(event) => setOrderDateRange((current) => ({
              ...current,
              start: event.target.value,
            }))}
          />
        </label>
        <label>
          <span>To Date</span>
          <input
            type="date"
            value={orderDateRange.end}
            min={orderDateRange.start || undefined}
            onChange={(event) => setOrderDateRange((current) => ({
              ...current,
              end: event.target.value,
            }))}
          />
        </label>
        {hasOrderDateFilter && (
          <button
            type="button"
            className="button small customer-transfer-date-clear"
            onClick={() => setOrderDateRange(getDefaultInvoiceDateRange())}
          >
            Clear
          </button>
        )}
      </div>
    )}

    {!ordersLoading && !ordersError && orders.length > 0 && filteredOrdersByDate.length === 0 && (
      <p style={{ opacity: 0.7 }}>No ship docs found for the selected date range.</p>
    )}

    {!ordersLoading && !ordersError && filteredOrdersByDate.length > 0 && (
      <div style={{ overflowX: 'auto' }}>
        <table className="inventory-table customer-ship-doc-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Date Created</th>
              <th
                className="collection-report-serial-header filter-header"
                aria-sort={shipDocInvoiceSortDirection === 'asc' ? 'ascending' : 'descending'}
              >
                <div className="collection-report-header-control">
                  <input
                    type="search"
                    value={shipDocInvoiceFilter}
                    placeholder="Invoices"
                    onChange={(event) => setShipDocInvoiceFilter(event.target.value)}
                    aria-label="Look up by invoice number"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="sort-arrow-button"
                    data-direction={shipDocInvoiceSortDirection === 'asc' ? 'asc' : 'desc'}
                    onClick={() => setShipDocInvoiceSortDirection((current) => (
                      current === 'asc' ? 'desc' : 'asc'
                    ))}
                    aria-label="Sort by invoice number"
                  />
                </div>
              </th>
              <th>Transaction Type</th>
              <th>Amount</th>
              <th>Date Last Modified</th>
              <th>Created By</th>
              <th>Last Modified By</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrdersByDate.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{renderTransactionTimestamp(r.datecreated || r.entrydate)}</td>
                <td>
                  {r.docno ? (
                    <button
                      type="button"
                      className="location-doc-link"
                      onClick={() => openShipDocInvoice(r)}
                    >
                      {r.docno}
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{r.trantype ?? '—'}</td>
                <td>
                  {r.amount != null
                    ? Number(String(r.amount).replace(/[$,]/g, '')).toLocaleString(undefined, {
                        style: 'currency',
                        currency: 'USD',
                      })
                    : '—'}
                </td>
                <td>{renderTransactionTimestamp(r.datelastmodified)}</td>
                <td>{r.usercreated ?? '—'}</td>
                <td>{r.userlastmodified ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

 {activeTab === 'invoices' && canViewTransfers && (
  <div className="customer-tab-panel">
    {invoicesLoading && <p>Loading transfers…</p>}

    {!invoicesLoading && invoicesError && (
      <p style={{ color: 'red' }}>{invoicesError}</p>
    )}

    {!invoicesLoading && !invoicesError && invoices.length === 0 && (
      <p style={{ opacity: 0.7 }}>No transfers found for this customer.</p>
    )}

    {!invoicesLoading && !invoicesError && invoices.length > 0 && (
      <div className="customer-transfer-date-range">
        <label>
          <span>From Date</span>
          <input
            type="date"
            value={invoiceDateRange.start}
            max={invoiceDateRange.end || undefined}
            onChange={(event) => setInvoiceDateRange((current) => ({
              ...current,
              start: event.target.value,
            }))}
          />
        </label>
        <label>
          <span>To Date</span>
          <input
            type="date"
            value={invoiceDateRange.end}
            min={invoiceDateRange.start || undefined}
            onChange={(event) => setInvoiceDateRange((current) => ({
              ...current,
              end: event.target.value,
            }))}
          />
        </label>
        {hasInvoiceDateFilter && (
          <button
            type="button"
            className="button small customer-transfer-date-clear"
            onClick={() => setInvoiceDateRange(getDefaultInvoiceDateRange())}
          >
            Clear
          </button>
        )}
      </div>
    )}

    {!invoicesLoading && !invoicesError && invoices.length > 0 && sortedInvoices.length === 0 && (
      <p style={{ opacity: 0.7 }}>No transfers found for the selected date range.</p>
    )}

    {!invoicesLoading && !invoicesError && sortedInvoices.length > 0 && (
      <div style={{ overflowX: 'auto' }}>
        <table className="inventory-table customer-transfers-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th className="customer-transfer-center">Date</th>
              <th
                className="filter-header"
                aria-sort={invoicesSort.key === 'from' ? (invoicesSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                onClick={() => sortInvoicesByParty('from')}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    sortInvoicesByParty('from');
                  }
                }}
                role="button"
                tabIndex={0}
              >
                From
                <button
                  type="button"
                  className="sort-arrow-button"
                  data-direction={invoiceSortArrow('from')}
                  aria-label="Sort by From"
                />
              </th>
              <th
                className="filter-header"
                aria-sort={invoicesSort.key === 'to' ? (invoicesSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                onClick={() => sortInvoicesByParty('to')}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    sortInvoicesByParty('to');
                  }
                }}
                role="button"
                tabIndex={0}
              >
                To
                <button
                  type="button"
                  className="sort-arrow-button"
                  data-direction={invoiceSortArrow('to')}
                  aria-label="Sort by To"
                />
              </th>
              <th className="customer-transfer-center">Items</th>
              <th className="customer-transfer-center">Quantity</th>
              <th className="customer-transfer-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {sortedInvoices.map((invoice) => (
              <tr key={invoice.id}>
                <td>
                  <button
                    type="button"
                    className="location-doc-link"
                    onClick={() => openInvoiceModal(invoice.id)}
                  >
                    {invoice.invoiceNo}
                  </button>
                </td>
                <td className="customer-transfer-center">{renderTransactionTimestamp(invoice.date)}</td>
                <td>{invoice.fromName || '—'}</td>
                <td>{invoice.toName || '—'}</td>
                <td className="customer-transfer-center">{invoice.itemCount ?? '—'}</td>
                <td className="customer-transfer-center">{invoice.totalQuantity ?? '—'}</td>
                <td className="customer-transfer-center">{formatMoney(invoice.totalAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

 {activeTab === 'repairOrders' && canViewRepairOrders && (
  <div className="customer-tab-panel">
    {repairOrdersLoading && <p>Loading repair orders...</p>}

    {!repairOrdersLoading && repairOrdersError && (
      <p style={{ color: 'red' }}>{repairOrdersError}</p>
    )}

    {!repairOrdersLoading && !repairOrdersError && repairOrders.length === 0 && (
      <p style={{ opacity: 0.7 }}>No repair orders found for this customer.</p>
    )}

    {!repairOrdersLoading && !repairOrdersError && repairOrders.length > 0 && (
      <div className="repair-orders-table-wrap" style={{ overflowX: 'auto' }}>
        <table className="inventory-table repair-orders-table repair-orders-selectable-table">
          <thead>
            <tr>
              <th>Repair Order #</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Serial</th>
              <th>Status</th>
              <th>Warranty</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {repairOrders.map((order) => {
              const isClosed = String(order.status || '').trim().toLowerCase() === 'closed';
              return (
                <tr
                  key={order.id}
                  className="repair-order-row"
                  role="link"
                  tabIndex={0}
                  aria-label={`Open repair order #${order.id} in a new tab`}
                  onClick={() => window.open(`/repair-orders?orderId=${encodeURIComponent(order.id)}`, '_blank', 'noopener,noreferrer')}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      window.open(`/repair-orders?orderId=${encodeURIComponent(order.id)}`, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <td>
                    <strong>{order.id}</strong>
                  </td>
                  <td>{renderTransactionTimestamp(order.dateCreated || order.orderDate)}</td>
                  <td>{order.customerName}</td>
                  <td>{order.serial}</td>
                  <td>
                    <div className="repair-list-status-cell">
                      <span>{order.status}</span>
                      {isClosed && order.closedAt && (
                        <small>{formatTransactionTimestampText(order.closedAt)}</small>
                      )}
                    </div>
                  </td>
                  <td>{order.warrantyStatus || '-'}</td>
                  <td>{formatMoney(order.total)}</td>
                  <td onClick={(event) => event.stopPropagation()}>
                    <button
                      type="button"
                      className="button small blue repair-invoice-list-button"
                      disabled={!isClosed}
                      title={isClosed ? 'Open invoice' : 'Invoice is available after the order is closed'}
                      onClick={() => openInvoiceModal(`RO-${order.id}`, { transactionTypeId: 640 })}
                    >
                      Invoice
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

 {activeTab === 'collectionReport' && canViewCustomerProfile && (
  <div className="customer-tab-panel">
    {collectionReportLoading && <p>Loading collection report...</p>}

    {!collectionReportLoading && collectionReportError && (
      <p style={{ color: 'red' }}>{collectionReportError}</p>
    )}

    {!collectionReportLoading && !collectionReportError && collectionReport && (
      <>
        {(collectionReport.rows || []).length === 0 ? (
          <p style={{ opacity: 0.7 }}>No collection report rows found for this customer.</p>
        ) : (
          <>
            <div className="collection-report-toolbar">
              <div className="collection-report-item-filter">
                <div className="collection-report-typeahead">
                  <input
                    type="text"
                    value={collectionItemFilter}
                    placeholder="Filter by Item/Service"
                    onChange={(event) => {
                      setCollectionItemFilter(event.target.value);
                      setCollectionItemFilterOpen(true);
                    }}
                    onFocus={() => setCollectionItemFilterOpen(true)}
                    onBlur={() => setTimeout(() => setCollectionItemFilterOpen(false), 150)}
                    aria-label="Filter collection report by item or service"
                    autoComplete="off"
                  />
                  {collectionItemFilterOpen && collectionItemSuggestions.length > 0 && (
                    <div className="collection-report-typeahead-menu">
                      {collectionItemSuggestions.map((item) => (
                        <button
                          type="button"
                          key={item}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            setCollectionItemFilter(item);
                            setCollectionItemFilterOpen(false);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="button collection-report-clear-button"
                  onClick={() => {
                    setCollectionItemFilter('');
                    setCollectionItemFilterOpen(false);
                  }}
                  disabled={!collectionItemFilter}
                >
                  Clear
                </button>
              </div>
              <div className="collection-report-top-totals">
                <span>
                  <strong>Pieces</strong>
                  {formatNumber(displayedCollectionTotalQuantity)}
                </span>
                <span>
                  <strong>Total</strong>
                  {formatMoney(displayedCollectionTotalAmount)}
                </span>
              </div>
              <CollectionReportDownload
                customerId={selectedCustomer?.nccustomerid}
                customerName={collectionReport.customerName}
                item={collectionItemFilter}
                serial={collectionSerialFilter}
                sort={collectionReportSort.key}
                direction={collectionReportSort.direction}
                onError={showAlert}
              />
              <button
                type="button"
                className="button collection-report-action-button"
                onClick={handlePrintCollectionReport}
              >
                Print
              </button>
              <CollectionReportEmailActions
                customerId={selectedCustomer?.nccustomerid}
                report={displayedCollectionReport}
                loading={collectionReportEmailLoading}
                buttonClassName="button collection-report-action-button"
                onLoadingChange={setCollectionReportEmailLoading}
                successMessage={(to) => `Collection report emailed to ${to}.`}
                onError={showAlert}
              />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="inventory-table customer-collection-report-table">
              <colgroup>
                <col className="collection-report-date-column" />
                <col className="collection-report-invoice-column" />
                <col className="collection-report-serial-column" />
                <col className="collection-report-item-column" />
                <col className="collection-report-quantity-column" />
                <col className="collection-report-price-column" />
                <col className="collection-report-total-column" />
              </colgroup>
              <thead>
                <tr>
                  <th
                    className="filter-header"
                    aria-sort={collectionReportSort.key === 'date' ? (collectionReportSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    onClick={() => sortCollectionReportBy('date')}
                    onKeyDown={(event) => {
                      if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        sortCollectionReportBy('date');
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Invoice Date
                    <button
                      type="button"
                      className="sort-arrow-button"
                      data-direction={collectionReportSortArrow('date')}
                      aria-label="Sort by Invoice Date"
                    />
                  </th>
                  <th>Invoice #</th>
                  <th
                    className="collection-report-serial-header filter-header"
                    aria-sort={collectionReportSort.key === 'serial' ? (collectionReportSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="collection-report-header-control">
                      <input
                        type="search"
                        value={collectionSerialFilter}
                        placeholder="Serial Number"
                        onChange={(event) => setCollectionSerialFilter(event.target.value)}
                        aria-label="Look up collection report by serial number"
                        autoComplete="off"
                      />
                      <button type="button" className="sort-arrow-button" data-direction={collectionReportSortArrow('serial')} onClick={() => sortCollectionReportBy('serial')} aria-label="Sort by Serial Number" />
                    </div>
                  </th>
                  <th
                    className="collection-report-item-header filter-header"
                    aria-sort={collectionReportSort.key === 'item' ? (collectionReportSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="collection-report-header-control">
                      <input
                        type="search"
                        value={collectionItemFilter}
                        placeholder="Item/Service"
                        onChange={(event) => setCollectionItemFilter(event.target.value)}
                        aria-label="Look up collection report by item or service"
                        autoComplete="off"
                      />
                      <button type="button" className="sort-arrow-button" data-direction={collectionReportSortArrow('item')} onClick={() => sortCollectionReportBy('item')} aria-label="Sort by Item or Service" />
                    </div>
                  </th>
                  <th>Qty</th>
                  <th>Price Per</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {sortedCollectionRows.length === 0 && (
                  <tr>
                    <td colSpan="7" className="collection-report-empty-row">
                      No collection report rows match the selected filters.
                    </td>
                  </tr>
                )}
                {sortedCollectionRows.map((row, index) => (
                  <tr key={`${row.documentId || row.invoiceNo}-${row.transactionTypeId || ''}-${row.serialNo || index}`}>
                    <td>{formatDate(row.invoiceDate)}</td>
                    <td>
                      {row.invoiceNo ? (
                        <button
                          type="button"
                          className="location-doc-link"
                          onClick={() => openCollectionReportDocument(row)}
                        >
                          {collectionDocumentLabel(row)}
                        </button>
                      ) : (
                        'â€”'
                      )}
                    </td>
                    <td>{row.serialNo || 'â€”'}</td>
                    <td>
                      <div>{row.itemService || 'â€”'}</div>
                      {row.repairNote && (
                        <div style={{ fontStyle: 'italic', marginTop: 4 }}>
                          Repair: {row.repairNote}
                        </div>
                      )}
                    </td>
                    <td>{formatNumber(row.quantity)}</td>
                    <td>{formatMoney(row.pricePer)}</td>
                    <td>{formatMoney(row.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4">Total</td>
                  <td>{formatNumber(displayedCollectionTotalQuantity)}</td>
                  <td></td>
                  <td>{formatMoney(displayedCollectionTotalAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          </>
        )}
      </>
    )}
  </div>
)}


    </div>

  </div>
)}

              </div>

              {selectedCustomer?.id === u.id && canDeleteCustomer && (
                <div className="employee-detail-panel">
                  <div className="employee-controls">
                    <button onClick={() => handleDelete(selectedCustomer.id)} className="inline-button delete-button">
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {customerLabelModal.open && customerLabelModal.customer && (
        <div
          className="invoice-modal-overlay print-modal-overlay"
          onClick={() => setCustomerLabelModal((current) => ({ ...current, open: false }))}
        >
          <div className="invoice-modal print-modal customer-label-modal" onClick={(event) => event.stopPropagation()}>
            <div className="invoice-modal-header">
              <h3>Print Label - {customerLabelModal.customer.name || customerLabelModal.customer.companyname || ''}</h3>
              <div className="invoice-modal-actions">
                <button
                  type="button"
                  className="button small blue"
                  onClick={() => printCustomerLabel(
                    customerLabelModal.customer,
                    customerLabelModal.labelType === 'pickup',
                    customerLabelModal.addressType
                  )}
                >
                  Print {customerLabelModal.labelType === 'pickup' ? 'Pick Up Label' : 'Delivery Label'}
                </button>
                <button
                  type="button"
                  className="button small red"
                  onClick={() => setCustomerLabelModal((current) => ({ ...current, open: false }))}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="customer-label-modal-controls">
              <label>
                <span>Address</span>
                <select
                  value={customerLabelModal.addressType}
                  onChange={(event) => setCustomerLabelModal((current) => ({
                    ...current,
                    addressType: event.target.value,
                  }))}
                >
                  <option value="billing">Address</option>
                  <option
                    value="shipping"
                    disabled={!hasCustomerShippingAddress(customerLabelModal.customer)}
                  >
                    Shipping Address
                  </option>
                </select>
              </label>
              <fieldset className="customer-label-type-options">
                <legend>Label</legend>
                <div>
                  <label>
                    <input
                      type="radio"
                      name="customer-label-type"
                      value="shipping"
                      checked={customerLabelModal.labelType === 'shipping'}
                      onChange={(event) => setCustomerLabelModal((current) => ({
                        ...current,
                        labelType: event.target.value,
                      }))}
                    />
                    <span>Delivery Label</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="customer-label-type"
                      value="pickup"
                      checked={customerLabelModal.labelType === 'pickup'}
                      onChange={(event) => setCustomerLabelModal((current) => ({
                        ...current,
                        labelType: event.target.value,
                      }))}
                    />
                    <span>Pick Up Label</span>
                  </label>
                </div>
              </fieldset>
            </div>
            <div className="transfer-output-preview customer-label-modal-preview">
              <TransferPrintLabel
                location={getCustomerLabelLocation(
                  customerLabelModal.customer,
                  customerLabelModal.addressType
                )}
                pickup={customerLabelModal.labelType === 'pickup'}
              />
            </div>
          </div>
        </div>
      )}

      {isCustomerTypeFilterOpen && (
        <div className="transfer-confirm-overlay" onClick={() => setIsCustomerTypeFilterOpen(false)}>
          <div className="transfer-confirm-modal customer-type-filter-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="transfer-modal-close"
              onClick={() => setIsCustomerTypeFilterOpen(false)}
            >
              x
            </button>
            <h3>Customer Types</h3>
            <div className="customer-type-filter-options">
              {customerTypes.map((type) => {
                const typeId = String(type.id);
                return (
                  <label key={typeId} className="customer-type-filter-option">
                    <input
                      type="checkbox"
                      checked={selectedCustomerTypeIds.includes(typeId)}
                      onChange={() => toggleCustomerTypeFilter(typeId)}
                    />
                    <span>{type.name}</span>
                  </label>
                );
              })}
            </div>
            <div className="transfer-confirm-actions">
              <button
                type="button"
                className="button small transfer-confirm-cancel"
                onClick={() => setSelectedCustomerTypeIds([])}
              >
                Clear
              </button>
              <button
                type="button"
                className="button small yellow"
                onClick={() => setSelectedCustomerTypeIds(customerTypes.map((type) => String(type.id)))}
              >
                Select All
              </button>
              <button
                type="button"
                className="button small blue"
                onClick={() => setIsCustomerTypeFilterOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {alertState.open && (
        <AlertModal message={alertState.message} onClose={() => setAlertState({ open: false, message: '' })} />
      )}
      {isAddCustomerOpen && (
        <div className="transfer-confirm-overlay" onClick={closeAddCustomerModal}>
          <form className="transfer-confirm-modal customer-add-modal" onSubmit={handleAddEmployee} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="transfer-modal-close"
              onClick={closeAddCustomerModal}
            >
              x
            </button>
            <h3>Add Customer</h3>
            <label>
              <span>Customer</span>
              <input
                className="transfer-confirm-input"
                type="text"
                value={newEmployee.companyname}
                onChange={(e) => setNewEmployee({ ...newEmployee, companyname: e.target.value })}
                autoFocus
                required
              />
            </label>
            <label>
              <span>Phone</span>
              <input
                className="transfer-confirm-input"
                type="tel"
                value={newEmployee.phone}
                onChange={(e) => {
                  setNewEmployee({ ...newEmployee, phone: e.target.value });
                  setDuplicatePhoneCustomers([]);
                  setCustomerAddStatus('');
                }}
              />
            </label>
            <label>
              <span>Email</span>
              <input
                className="transfer-confirm-input"
                type="email"
                value={newEmployee.email}
                onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
              />
            </label>
            <label>
              <span>Customer Type</span>
              <select
                className="transfer-confirm-input"
                value={newEmployee.customertypeid || getDefaultCustomerTypeId(creatableCustomerTypes)}
                onChange={(e) => {
                  setNewEmployee({ ...newEmployee, customertypeid: e.target.value });
                  setDuplicatePhoneCustomers([]);
                  setCustomerAddStatus('');
                }}
              >
                {creatableCustomerTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </label>
            {customerAddStatus && <p className="transfer-confirm-error">{customerAddStatus}</p>}
            {duplicatePhoneCustomers.length > 0 && (
              <div className="customer-phone-matches" role="alert">
                <strong>Customers with this phone number</strong>
                <ul>
                  {duplicatePhoneCustomers.map((customer) => (
                    <li key={customer.id}>
                      <strong>{customer.name}</strong> (#{customer.id})
                      <div>Phone: {customer.phone || '—'}</div>
                      {customer.altphone && <div>Alternate: {customer.altphone}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="transfer-confirm-actions">
              <button
                type="button"
                className="button small transfer-confirm-cancel"
                onClick={closeAddCustomerModal}
              >
                Cancel
              </button>
              <button type="submit" className="button small blue">
                Add Customer
              </button>
            </div>
          </form>
        </div>
      )}
      {deleteCustomerConfirm.open && (
        <div className="transfer-confirm-overlay" onClick={closeDeleteCustomerConfirm}>
          <form className="transfer-confirm-modal" onSubmit={deleteCustomer} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="transfer-modal-close"
              onClick={closeDeleteCustomerConfirm}
              disabled={deleteCustomerLoading}
            >
              x
            </button>
            <h3>Delete Customer</h3>
            <p>
              Type customer name <strong>{deleteCustomerConfirm.customer?.name}</strong> to delete this customer.
            </p>
            <input
              className="transfer-confirm-input"
              type="text"
              value={deleteCustomerConfirm.value}
              onChange={(e) => setDeleteCustomerConfirm((current) => ({
                ...current,
                value: e.target.value,
                error: '',
              }))}
              autoFocus
            />
            {deleteCustomerConfirm.error && (
              <p className="transfer-confirm-error">{deleteCustomerConfirm.error}</p>
            )}
            <div className="transfer-confirm-actions">
              <button
                type="button"
                className="button small transfer-confirm-cancel"
                onClick={closeDeleteCustomerConfirm}
                disabled={deleteCustomerLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button small red"
                disabled={
                  deleteCustomerLoading
                  || String(deleteCustomerConfirm.value || '').trim()
                    !== String(deleteCustomerConfirm.customer?.name || '').trim()
                }
              >
                {deleteCustomerLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </form>
        </div>
      )}
      {invoiceModal.open && (
        <div className="invoice-modal-overlay print-modal-overlay transfer-invoice-document" onClick={closeInvoiceModal}>
          <div
            ref={invoiceModalRef}
            className="invoice-modal print-modal customer-invoice-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <section className="shipdoc-invoice-workspace">
              <div className="invoice-modal-header">
                <div>
                  <h3>
                    {invoiceModal.invoice
                      ? `${invoiceModal.invoice.type || 'Invoice'} #${invoiceModal.invoice.invoiceNo}`
                      : 'Invoice'}
                  </h3>
                  {isRepairInvoiceDocument(invoiceModal.invoice) && (
                    <div className="transfer-output-tabs" role="tablist" aria-label="Repair invoice print documents">
                      {[
                        ['invoice', 'Invoice'],
                        ['shippingLabel', 'Delivery Label'],
                        ['pickupLabel', 'Pick Up Label'],
                      ].map(([view, label]) => (
                        <button
                          key={view}
                          type="button"
                          role="tab"
                          aria-selected={invoicePrintView === view}
                          className={invoicePrintView === view ? 'active' : ''}
                          onClick={() => setInvoicePrintView(view)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="invoice-modal-actions">
                  <InvoicePrintEmailActions
                    invoice={invoiceModal.invoice}
                    disabled={!invoiceModal.invoice}
                    loading={invoiceEmailLoading}
                    onLoadingChange={setInvoiceEmailLoading}
                    onPrint={printCustomerInvoiceOutput}
                    printLabel={`Print ${invoicePrintView === 'invoice'
                      ? 'Invoice'
                      : invoicePrintView === 'shippingLabel'
                        ? 'Delivery Label'
                        : 'Pick Up Label'}`}
                    successMessage={(to) => `${invoiceModal.invoice?.type || 'Invoice'} #${invoiceModal.invoice?.invoiceNo} emailed to ${to}.`}
                    onError={showAlert}
                  />
                  {canEditInvoiceTax && invoicePrintView === 'invoice' && (
                    <button
                      type="button"
                      className="button small yellow"
                      onClick={openTaxEditor}
                      disabled={invoiceModal.loading}
                    >
                      Edit Tax
                    </button>
                  )}
                  <button
                    type="button"
                    className="button small red"
                    onClick={closeInvoiceModal}
                  >
                    Close
                  </button>
                </div>
              </div>

              {invoiceModal.loading && <p className="invoice-modal-status">Loading invoice…</p>}
              {!invoiceModal.loading && invoiceModal.error && (
                <p className="invoice-modal-status error">{invoiceModal.error}</p>
              )}
              {!invoiceModal.loading && invoiceModal.invoice && invoicePrintView === 'invoice' && (
                <ShipDocInvoiceDocument invoice={invoiceModal.invoice} highlightSerial={invoiceHighlightSerial} />
              )}
              {!invoiceModal.loading && isRepairInvoiceDocument(invoiceModal.invoice) && invoicePrintView === 'shippingLabel' && (
                <div className="transfer-output-preview transfer-opened-label-preview">
                  <TransferPrintLabel location={invoiceModal.invoice.to} services />
                </div>
              )}
              {!invoiceModal.loading && isRepairInvoiceDocument(invoiceModal.invoice) && invoicePrintView === 'pickupLabel' && (
                <div className="transfer-output-preview transfer-opened-label-preview">
                  <TransferPrintLabel location={invoiceModal.invoice.to} pickup services />
                </div>
              )}
            </section>
          </div>
        </div>
      )}
      {taxEditor.open && (
        <div className="invoice-alert-overlay" onClick={closeTaxEditor}>
          <form className="invoice-tax-modal" onSubmit={saveInvoiceTax} onClick={(event) => event.stopPropagation()}>
            <div className="invoice-tax-modal-title">
              <h3>Edit Invoice Tax</h3>
              <button type="button" onClick={closeTaxEditor} disabled={taxEditor.saving}>x</button>
            </div>
            <label>
              Tax
              <select
                value={taxEditor.value}
                onChange={(event) => setTaxEditor((current) => ({ ...current, value: event.target.value, error: '' }))}
                autoFocus
                required
              >
                <option value="0">0%</option>
                {preferencesTaxRate > 0 && (
                  <option value={String(preferencesTaxRate)}>{String(preferencesTaxRate)}%</option>
                )}
              </select>
            </label>
            {taxEditor.error && <p className="invoice-tax-error">{taxEditor.error}</p>}
            <div className="invoice-tax-actions">
              <button type="button" className="button small gray" onClick={closeTaxEditor} disabled={taxEditor.saving}>
                Cancel
              </button>
              <button type="submit" className="button small blue" disabled={taxEditor.saving}>
                {taxEditor.saving ? 'Applying...' : 'Apply Tax'}
              </button>
            </div>
          </form>
        </div>
      )}
      {creditMemoModal.open && (
        <div className="invoice-modal-overlay print-modal-overlay" onClick={closeCreditMemoModal}>
          <div
            className="invoice-modal print-modal customer-credit-memo-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="invoice-modal-header">
              <strong>
                {creditMemoModal.transfer
                  ? `${creditMemoModal.transfer.documentTitle || 'Credit Memo'} #${creditMemoModal.transfer.docNo}`
                  : `Credit Memo #${creditMemoModal.docNo}`}
              </strong>
              <div className="invoice-modal-actions">
                <InvoicePrintEmailActions
                  invoice={transferToEmailInvoice(creditMemoModal.transfer)}
                  disabled={!creditMemoModal.transfer}
                  loading={collectionReportEmailLoading}
                  onPrint={printCreditMemoDocument}
                  onLoadingChange={setCollectionReportEmailLoading}
                  successMessage={(to) => `${creditMemoModal.transfer?.documentTitle || 'Credit Memo'} #${creditMemoModal.transfer?.docNo} emailed to ${to}.`}
                  onError={showAlert}
                />
                <button
                  type="button"
                  className="button small red"
                  onClick={closeCreditMemoModal}
                >
                  Close
                </button>
              </div>
            </div>

            {creditMemoModal.loading && <p className="invoice-modal-status">Loading credit memo...</p>}
            {!creditMemoModal.loading && creditMemoModal.error && (
              <p className="invoice-modal-status error">{creditMemoModal.error}</p>
            )}
            {!creditMemoModal.loading && creditMemoModal.transfer && (
              <section className="shipdoc-invoice-workspace">
                <ShipDocInvoiceDocument
                  transfer={creditMemoModal.transfer}
                  highlightSerial={creditMemoHighlightSerial}
                />
              </section>
            )}
          </div>
        </div>
      )}
      {receiveDocModal.open && (
        <div className="transfer-modal-overlay" onClick={closeReceiveDocModal}>
          <div className="transfer-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="transfer-modal-close" onClick={closeReceiveDocModal}>
              x
            </button>

            {receiveDocModal.loading && <p className="transfer-status">Loading DocNo {receiveDocModal.docNo}...</p>}
            {!receiveDocModal.loading && receiveDocModal.error && (
              <p className="transfer-status">{receiveDocModal.error}</p>
            )}

            {!receiveDocModal.loading && !receiveDocModal.error && receiveDocModal.transfer && (
              <section className="inventory-card transfer-workspace">
                <div className="transfer-header">
                  <div className="transfer-field-group transfer-party-fields">
                    <label>
                      From Location
                      <input value={receiveDocModal.transfer.fromLocation || receiveDocModal.transfer.vendor || ''} readOnly />
                    </label>
                    <label>
                      Pay To
                      <input value={receiveDocModal.transfer.payTo || ''} readOnly />
                    </label>
                    <label>
                      Receive To Location
                      <input value={receiveDocModal.transfer.receiveLocation || ''} readOnly />
                    </label>
                  </div>

                  <div className="transfer-doc-card">
                    <h2>Receive Items</h2>
                    <div className="transfer-doc-grid">
                      <label>
                        DocNo
                        <input value={receiveDocModal.transfer.docNo || receiveDocModal.docNo || ''} readOnly />
                      </label>
                      <label>
                        Receive Date
                        <input type="date" value={formatDateInput(receiveDocModal.transfer.receiveDate)} readOnly />
                      </label>
                      <span className="transfer-note">Not Recorded in QuickBooks</span>
                      <label className="transfer-check">
                        <input type="checkbox" checked={Boolean(receiveDocModal.transfer.received)} readOnly />
                        Received
                      </label>
                      <label className="transfer-check">
                        <input type="checkbox" checked={Boolean(receiveDocModal.transfer.ncOnly)} readOnly />
                        NC Only
                      </label>
                    </div>
                  </div>
                </div>

                <div className="transfer-meta-row">
                  <label>
                    Ship Method
                    <input readOnly />
                  </label>
                  <label>
                    Class
                    <input readOnly />
                  </label>
                  <label>
                    Terms
                    <input readOnly />
                  </label>
                  <label>
                    Expected Date
                    <input type="date" value={formatDateInput(receiveDocModal.transfer.expectedDate)} readOnly />
                  </label>
                  <label>
                    Vendor Reference
                    <input readOnly />
                  </label>
                  <label>
                    Ship Doc Reference
                    <input readOnly />
                  </label>
                  <label>
                    # Pckg
                    <input value={(receiveDocModal.transfer.items || []).length} readOnly />
                  </label>
                  <label>
                    Weight
                    <input readOnly />
                  </label>
                </div>

                <div className="transfer-table-wrap">
                  <table className="inventory-table transfer-table">
                    <thead>
                      <tr>
                        <th>Line #</th>
                        <th>Item</th>
                        <th>Description</th>
                        <th>Serial #</th>
                        <th>Vend Part #</th>
                        <th>Qty</th>
                        <th>Ordered</th>
                        <th>Back Order</th>
                        <th>UOM</th>
                        <th>Cost Per</th>
                        <th>Total Cost</th>
                        <th>PO #</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupReceiveItems(receiveDocModal.transfer.items || []).map((item) => (
                        <tr key={item.id}>
                          <td>{item.lineNo}</td>
                          <td>{item.item}</td>
                          <td>{item.description}</td>
                          <td>{serialListText(item) || '-'}</td>
                          <td>{item.vendorPart}</td>
                          <td>{formatNumber(item.quantity)}</td>
                          <td>{formatNumber(item.ordered)}</td>
                          <td>{formatNumber(item.backOrder)}</td>
                          <td>{item.uom}</td>
                          <td>{formatMoney(item.costPer)}</td>
                          <td>{formatMoney(item.totalCost)}</td>
                          <td>{item.poNo}</td>
                          <td className="transfer-details-cell">v</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="transfer-footer">
                  <span>Subtotal&nbsp; {formatMoney(receiveDocModal.transfer.subtotal)}</span>
                  <span>Charges&nbsp; {formatMoney(receiveDocModal.transfer.charges)}</span>
                  <strong>Total&nbsp; {formatMoney(receiveDocModal.transfer.total)}</strong>
                </div>
              </section>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CustomersPage;
